/**
 * WorkfrontModelTab — Shared 3D Model + selection + allocation UI.
 * Used by Piping Workfront (PWM) and Structural Workfront (SWM).
 *
 * Features:
 *   - APS model upload (file/URN) with live progress bar
 *   - Embedded the 3D model service GuiViewer3D
 *   - Resizable left rail: TREE (model browser) + PROPERTIES + FILTER tabs
 *   - Dynamic property extraction → AND-filter chips
 *   - Tree filterable by property values
 *   - Property→Domain mapping wizard (Line/Spool/Tag/Mark)
 *   - Status Heatmap toggle (PWM/SWM gates colorize 3D; non-applicable ghosted)
 *   - Bulk extract: roll selected dbIds → Lines+Spools (piping) / Tags+Marks (structural)
 *   - Save selections + create allocations
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Upload, Link as LinkIcon, Box, Filter, Save, Trash2, Send, Loader2,
  Search, X, RefreshCw, AlertTriangle, CheckCircle2, Layers, Wrench,
  TreeDeciduous, Tag, Settings as SettingsIcon, Palette, Eye, EyeOff,
  ChevronRight, ChevronDown, Sparkles, ListTree, Info,
} from 'lucide-react';
import MTMElementPopover from './MTMElementPopover';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import api from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════
// Viewer wrapper
// ═══════════════════════════════════════════════════════════════
function WorkfrontViewer({ projectId, moduleKey, modelUrn, translationStatus, isDark, onViewerReady, onSelect, viewerRef }) {
  const containerRef = useRef(null);
  const [viewerError, setViewerError] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Human-readable decode for the Panorix 3D viewer numeric error codes so silent
  // 3D failures (typically token scope / derivative issues) surface clearly.
  const VIEWER_ERR = {
    1: 'UNKNOWN_FAILURE', 2: 'BAD_DATA', 3: 'NETWORK_FAILURE',
    4: 'NETWORK_ACCESS_DENIED (token lacks viewables:read + data:read)',
    5: 'NETWORK_FILE_NOT_FOUND', 6: 'NETWORK_SERVER_ERROR',
    7: 'NETWORK_UNHANDLED_RESPONSE_CODE (token missing data:read scope, or derivative not ready)',
    8: 'BROWSER_WEBGL_NOT_SUPPORTED', 9: 'BAD_DATA_NO_VIEWABLE_CONTENT',
    10: 'BROWSER_WEBGL_DISABLED', 11: 'BAD_DATA_MODEL_IS_EMPTY', 12: 'RTC_ERROR',
  };
  const decodeErr = (err) => {
    if (err == null) return 'Unknown error';
    const code = typeof err === 'number' ? err : (err?.code ?? err?.errorCode);
    if (code != null && VIEWER_ERR[code]) return `Code ${code} · ${VIEWER_ERR[code]}`;
    return err?.message || String(err);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !modelUrn || translationStatus !== 'COMPLETE' || !window.Autodesk) return;

    setViewerError(null); setInitializing(true);
    const options = {
      env: 'AutodeskProduction2',
      api: 'streamingV2',
      getAccessToken: async (cb) => {
        try {
          // Authenticated client — the token endpoint now requires a Panorix
          // session and membership of this project.
          const { data: d } = await api.get(
            `/v1/projects/${projectId}/workfront-model/${moduleKey}/viewer-token`);
          cb(d.token, d.expiresIn);
        } catch (e) {
          const status = e?.response?.status;
          setViewerError(status === 403
            ? 'You do not have access to this project\u2019s 3D model.'
            : `Viewer token failed${status ? ` (HTTP ${status})` : ''} — ${
              e?.response?.data?.detail || e.message}`);
          setInitializing(false);
        }
      },
    };

    let v, selHandler = null;
    window.Autodesk.Viewing.Initializer(options, () => {
      if (!containerRef.current) return;
      v = new window.Autodesk.Viewing.GuiViewer3D(containerRef.current, { theme: isDark ? 'dark-theme' : 'light-theme' });
      v.start();
      const bg = isDark ? '#0A0E1A' : '#1F2937';
      const r = parseInt(bg.slice(1, 3), 16);
      const g = parseInt(bg.slice(3, 5), 16);
      const b = parseInt(bg.slice(5, 7), 16);
      v.setBackgroundColor(r, g, b, r, g, b);

      window.Autodesk.Viewing.Document.load(`urn:${modelUrn}`, (doc) => {
        const viewable = doc.getRoot().getDefaultGeometry();
        if (!viewable) {
          setViewerError('No 3D viewable found in translated model.');
          setInitializing(false);
          return;
        }
        v.loadDocumentNode(doc, viewable).then(() => {
          if (viewerRef) viewerRef.current = v;
          setInitializing(false);
          onViewerReady && onViewerReady(v);
        }).catch((err) => {
          console.error('loadDocumentNode failed', err);
          setViewerError(`Failed to load 3D geometry — ${decodeErr(err)}`);
          setInitializing(false);
        });
        selHandler = (e) => onSelect && onSelect((e && e.dbIdArray) || v.getSelection() || []);
        v.addEventListener(window.Autodesk.Viewing.SELECTION_CHANGED_EVENT, selHandler);
      }, (err) => {
        console.error('doc load fail', err);
        setViewerError(`Failed to load model document — ${decodeErr(err)}`);
        setInitializing(false);
      });
    });

    return () => { try { if (v && selHandler) v.removeEventListener(window.Autodesk.Viewing.SELECTION_CHANGED_EVENT, selHandler); if (v) v.finish(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelUrn, translationStatus, reloadKey]);

  return (
    <div ref={containerRef} className="absolute inset-0" data-testid="workfront-viewer-canvas">
      {initializing && !viewerError && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 5 }}>
          <div className="text-center space-y-2">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-steel-500" />
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Streaming 3D geometry from the 3D model service…</p>
          </div>
        </div>
      )}
      {viewerError && (
        <div className="absolute inset-0 flex items-center justify-center p-6" style={{ zIndex: 10, background: 'rgba(10,14,26,0.85)' }}
             data-testid="workfront-viewer-error">
          <div className="max-w-md text-center space-y-4">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>3D Viewer Error</h3>
            <p className="text-xs font-mono px-3 py-2 rounded" style={{ color: 'var(--text-primary)', background: 'rgba(0,0,0,0.35)' }}>{viewerError}</p>
            <button onClick={() => { setViewerError(null); setReloadKey(k => k + 1); }}
              className="px-3 py-1.5 rounded-md text-xs font-semibold bg-ember-500 text-white hover:bg-ember-600 inline-flex items-center gap-1.5"
              data-testid="workfront-viewer-retry">
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Property extraction
//
// Strategy: query the APS PropertyDatabase **directly** in the worker
// via `executeUserFunction`. This bypasses every viewer-side filter
// (ignoreHidden, hidden-flag, leaf-only restrictions of `getBulkProperties2`)
// and returns EVERY category & attribute that exists in the SVF2 — including
// PDMS Attribute, Construction, Material, custom Navisworks roll-ups, etc.
//
// `getBulkProperties2` is kept as a fallback when running against an older
// APS Viewer SDK that doesn't expose `getPropertyDb()`.
// ═══════════════════════════════════════════════════════════════

/* This function is serialized and executed inside the APS web-worker, so it
 * must be fully self-contained — no closure references to outer variables. */
function _propertyDbUserFn(pdb) {
  const properties = {};   // "Cat ▸ Name" → { key, category, name, uniqueValues:[], dbIdsByValue:{} }
  const propsByDbId = {};  // dbId → { "Cat ▸ Name": value }
  const attrs = {};        // attrId → { category, name }

  pdb.enumAttributes((attrId, attrDef) => {
    const cat = attrDef.displayCategory || attrDef.category || 'General';
    const name = attrDef.displayName || attrDef.name || ('attr_' + attrId);
    attrs[attrId] = { category: cat, name: name };
  });

  pdb.enumObjects((dbId) => {
    pdb.enumObjectProperties(dbId, (attrId, valueId) => {
      const a = attrs[attrId];
      if (!a) return;
      let val = pdb.getAttrValue(attrId, valueId);
      if (val === null || val === undefined || val === '') return;
      val = (typeof val === 'object') ? JSON.stringify(val) : String(val);
      const key = a.category + ' \u25b8 ' + a.name;
      let p = properties[key];
      if (!p) {
        p = { key: key, category: a.category, name: a.name, uniqueValues: [], dbIdsByValue: {} };
        properties[key] = p;
      }
      if (!p.dbIdsByValue[val]) {
        p.dbIdsByValue[val] = [];
        if (p.uniqueValues.length < 2000) p.uniqueValues.push(val);
      }
      p.dbIdsByValue[val].push(dbId);
      if (!propsByDbId[dbId]) propsByDbId[dbId] = {};
      propsByDbId[dbId][key] = val;
    });
  });

  return { properties: properties, propsByDbId: propsByDbId };
}

async function _extractViaBulkProperties(viewer) {
  // Fallback path — scans ALL nodes (not just leaves), since PDMS attributes
  // typically live on grouping/parent nodes (Site, Zone, Pipe), never on leaves.
  return new Promise((resolve) => {
    viewer.getObjectTree((tree) => {
      const allIds = [];
      const dive = (id) => {
        allIds.push(id);
        tree.enumNodeChildren(id, dive);
      };
      dive(tree.getRootId());
      viewer.model.getBulkProperties2(allIds, { ignoreHidden: false }, (results) => {
        const propMap = {};
        const propsByDbId = {};
        for (const r of results) {
          propsByDbId[r.dbId] = {};
          for (const p of r.properties || []) {
            const cat = p.displayCategory || p.category || 'General';
            const name = p.displayName || p.attributeName || 'unknown';
            const key = `${cat} \u25b8 ${name}`;
            const val = typeof p.displayValue === 'number' ? p.displayValue : (p.displayValue ?? '').toString();
            if (val === '') continue;
            if (!propMap[key]) propMap[key] = { key, category: cat, name, values: new Set(), dbIdsByValue: {} };
            propMap[key].values.add(val);
            (propMap[key].dbIdsByValue[val] ||= []).push(r.dbId);
            propsByDbId[r.dbId][key] = val;
          }
        }
        const properties = {};
        for (const [k, v] of Object.entries(propMap)) {
          properties[k] = { key: v.key, category: v.category, name: v.name, uniqueValues: Array.from(v.values).slice(0, 2000), dbIdsByValue: v.dbIdsByValue };
        }
        resolve({ properties, propsByDbId, leaves: allIds });
      }, () => resolve({ properties: {}, propsByDbId: {}, leaves: [] }));
    }, () => resolve({ properties: {}, propsByDbId: {}, leaves: [] }));
  });
}

async function extractAll(viewer) {
  if (!viewer || !viewer.model) return { properties: {}, leaves: [], propsByDbId: {} };
  // Primary path — PropertyDb direct query (returns truly every category).
  try {
    const pdb = typeof viewer.model.getPropertyDb === 'function' ? viewer.model.getPropertyDb() : null;
    if (pdb && typeof pdb.executeUserFunction === 'function') {
      const res = await pdb.executeUserFunction(_propertyDbUserFn);
      if (res && res.properties && Object.keys(res.properties).length > 0) {
        return { properties: res.properties, propsByDbId: res.propsByDbId || {}, leaves: [] };
      }
    }
  } catch (e) {
    // Fall through to bulk-properties fallback
    console.warn('PropertyDb direct query failed, falling back to getBulkProperties2', e);
  }
  return _extractViaBulkProperties(viewer);
}

// ═══════════════════════════════════════════════════════════════
// Model Tree component — recursive collapsible browser
// ═══════════════════════════════════════════════════════════════
function ModelTreeNode({ nodeId, tree, depth, expanded, setExpanded, viewer, search, visibleSet, selectedSet, selectedHostRef }) {
  const name = tree.getNodeName(nodeId);
  const childCount = tree.getChildCount(nodeId);
  const isExpanded = expanded.has(nodeId);
  const lowerName = (name || '').toLowerCase();
  const matchesSearch = !search || lowerName.includes(search.toLowerCase());
  const hasMatch = matchesSearch || (visibleSet && visibleSet.has(nodeId));
  const isSelected = selectedSet && selectedSet.has(nodeId);

  // ⚠️ Hooks must be declared unconditionally and BEFORE any early return.
  const rowRef = useRef(null);
  useEffect(() => {
    if (isSelected && rowRef.current && selectedHostRef?.current) {
      try { rowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch {}
    }
  }, [isSelected, selectedHostRef]);

  if (!hasMatch && search && visibleSet && !visibleSet.has(nodeId)) return null;

  const toggle = () => setExpanded(prev => { const n = new Set(prev); if (n.has(nodeId)) n.delete(nodeId); else n.add(nodeId); return n; });
  const selectNode = (e) => {
    e.stopPropagation();
    if (!viewer) return;
    try {
      if (e.shiftKey && childCount > 0) {
        // Shift-click → select the entire subtree (all descendant leaves).
        // Useful for grabbing a whole line / area / discipline in one click.
        const allLeaves = [];
        const dive = (id) => { if (tree.getChildCount(id) === 0) allLeaves.push(id); else tree.enumNodeChildren(id, dive); };
        dive(nodeId);
        if (allLeaves.length === 0) allLeaves.push(nodeId);
        viewer.select(allLeaves);
        viewer.fitToView(allLeaves);
      } else {
        // Plain click → single-node select. Properties panel will reflect this exact node.
        viewer.select([nodeId]);
        viewer.fitToView([nodeId]);
      }
    } catch {}
  };

  const children = [];
  if (isExpanded && childCount > 0) {
    tree.enumNodeChildren(nodeId, (cid) => { children.push(cid); });
  }

  return (
    <div data-testid={`tree-node-${nodeId}`}>
      <div
        ref={rowRef}
        className={`flex items-center gap-1 py-0.5 rounded px-1 cursor-pointer text-xs select-none transition-colors ${isSelected ? 'bg-ember-500/15 ring-1 ring-ember-500/40' : 'hover:bg-steel-500/10'}`}
        style={{ paddingLeft: `${depth * 12 + 4}px`, color: isSelected ? 'var(--text-primary)' : 'var(--text-secondary)' }}
        onClick={selectNode}
        data-selected={isSelected || undefined}>
        {childCount > 0 ? (
          <button onClick={(e) => { e.stopPropagation(); toggle(); }} className="shrink-0 w-4 h-4 flex items-center justify-center">
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : <span className="w-4 inline-block" />}
        <Box className={`w-3 h-3 shrink-0 ${isSelected ? 'text-ember-500' : 'text-steel-500'}`} />
        <span className={`truncate flex-1 ${matchesSearch && search ? 'text-ember-500 font-medium' : ''} ${isSelected ? 'font-semibold' : ''}`}>{name || `Node ${nodeId}`}</span>
        {childCount > 0 && <span className="text-[9px] font-mono shrink-0" style={{ color: 'var(--text-muted)' }}>{childCount}</span>}
      </div>
      {children.map(cid => (
        <ModelTreeNode key={cid} nodeId={cid} tree={tree} depth={depth + 1}
          expanded={expanded} setExpanded={setExpanded} viewer={viewer} search={search}
          visibleSet={visibleSet} selectedSet={selectedSet} selectedHostRef={selectedHostRef}/>
      ))}
    </div>
  );
}

function ModelTree({ viewer, search, filteredDbIds, selectedDbIds }) {
  const [rootId, setRootId] = useState(null);
  const [tree, setTree] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const hostRef = useRef(null);

  useEffect(() => {
    if (!viewer) return;
    viewer.getObjectTree((t) => {
      setTree(t);
      const r = t.getRootId();
      setRootId(r);
      const init = new Set([r]);
      t.enumNodeChildren(r, (c) => { init.add(c); }, false);
      setExpanded(init);
    });
  }, [viewer]);

  // Build selected set = the picked dbIds + all their ancestors (so the path is visible).
  // Also auto-expand all ancestors when selection changes.
  const selectedSet = useMemo(() => {
    if (!tree || !selectedDbIds || selectedDbIds.length === 0) return null;
    const sel = new Set(selectedDbIds);
    const withAncestors = new Set(sel);
    sel.forEach((id) => {
      let p = tree.getNodeParentId(id);
      while (p !== undefined && p !== null && p !== 0) {
        withAncestors.add(p);
        p = tree.getNodeParentId(p);
      }
    });
    return withAncestors;
  }, [tree, selectedDbIds]);

  // Whenever selection changes, ensure all ancestors of selected items are expanded
  useEffect(() => {
    if (!tree || !selectedDbIds || selectedDbIds.length === 0) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      selectedDbIds.forEach((id) => {
        let p = tree.getNodeParentId(id);
        while (p !== undefined && p !== null && p !== 0) {
          next.add(p);
          p = tree.getNodeParentId(p);
        }
      });
      return next;
    });
  }, [tree, selectedDbIds]);

  // visibleSet for search/filter narrowing
  const visibleSet = useMemo(() => {
    if (!tree || (!search && (!filteredDbIds || filteredDbIds.length === 0))) return null;
    const matches = new Set();
    const collectAll = (id) => {
      const name = (tree.getNodeName(id) || '').toLowerCase();
      const filterMatch = filteredDbIds && filteredDbIds.length > 0 && filteredDbIds.includes(id);
      const searchMatch = search && name.includes(search.toLowerCase());
      if (filterMatch || searchMatch) matches.add(id);
      tree.enumNodeChildren(id, collectAll);
    };
    collectAll(tree.getRootId());
    const visible = new Set(matches);
    matches.forEach((m) => {
      let p = tree.getNodeParentId(m);
      while (p !== undefined && p !== null && p !== 0) {
        visible.add(p);
        p = tree.getNodeParentId(p);
      }
    });
    return visible;
  }, [tree, search, filteredDbIds]);

  if (!tree || !rootId) {
    return <div className="text-xs p-3" style={{ color: 'var(--text-muted)' }}><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5"/> Loading model hierarchy…</div>;
  }
  return (
    <div ref={hostRef} className="text-xs overflow-auto max-h-[460px] pr-1" data-testid="model-tree">
      <ModelTreeNode nodeId={rootId} tree={tree} depth={0}
        expanded={expanded} setExpanded={setExpanded}
        viewer={viewer} search={search}
        visibleSet={visibleSet} selectedSet={selectedSet} selectedHostRef={hostRef}/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Properties Panel — shows props of currently selected object
// ═══════════════════════════════════════════════════════════════
function PropertiesPanel({ viewer, selectedDbIds, onAddFilter }) {
  const [props, setProps] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastIdRef = useRef(null);

  useEffect(() => {
    if (!viewer || !selectedDbIds || selectedDbIds.length === 0) { setProps(null); return; }
    const dbid = selectedDbIds[0];
    if (lastIdRef.current === dbid) return;
    lastIdRef.current = dbid;
    setLoading(true);
    // Use getProperties2 (richer payload, includes hidden/internal properties).
    // Falls back to getProperties for older viewer SDKs.
    const onOk = (data) => { setProps(data); setLoading(false); };
    const onErr = () => setLoading(false);
    if (typeof viewer.model?.getProperties2 === 'function') {
      viewer.model.getProperties2(dbid, onOk, onErr, { needsExternalId: true });
    } else if (typeof viewer.getProperties2 === 'function') {
      viewer.getProperties2(dbid, onOk, onErr);
    } else {
      viewer.getProperties(dbid, onOk, onErr);
    }
  }, [viewer, selectedDbIds]);

  if (loading) return <div className="text-xs p-3" style={{ color: 'var(--text-muted)' }}><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5"/> Loading properties…</div>;
  if (!props) return <div className="text-xs p-3" style={{ color: 'var(--text-muted)' }}>Click an object in the viewer or tree to see its properties.</div>;

  // Show EVERY property (including hidden/internal ones the viewer normally filters out).
  // We deduplicate by (displayCategory + displayName + displayValue) to avoid noise from APS multi-instance attributes.
  const seen = new Set();
  const allProps = (props.properties || []).filter((p) => {
    const k = `${p.displayCategory || 'General'}|${p.displayName}|${p.displayValue}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const grouped = allProps.reduce((acc, p) => {
    const cat = p.displayCategory || 'General';
    (acc[cat] ||= []).push(p);
    return acc;
  }, {});
  // Stable category order: General, Item, Element, then alphabetical
  const PRIORITY_CATS = ['General', 'Item', 'Element', 'Identity Data', 'Constraints', 'Dimensions'];
  const catOrder = [
    ...PRIORITY_CATS.filter(c => grouped[c]),
    ...Object.keys(grouped).filter(c => !PRIORITY_CATS.includes(c)).sort(),
  ];

  return (
    <div className="text-xs overflow-auto max-h-[460px] pr-1" data-testid="properties-panel">
      <div className="mb-3 pb-2 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
        <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{props.name || `Object ${props.dbId}`}</div>
        <div className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>
          dbId <span className="text-ember-500">{props.dbId}</span>
          {props.externalId ? <span> · ext {props.externalId.slice(0, 24)}{props.externalId.length > 24 ? '…' : ''}</span> : null}
          {selectedDbIds.length > 1 ? ` · +${selectedDbIds.length - 1} more` : ''}
          {' · '}<span className="text-steel-500">{allProps.length}</span> properties
        </div>
      </div>
      {catOrder.map((cat) => (
        <div key={cat} className="mb-3">
          <div className="text-eyebrow mb-1.5">{cat}</div>
          {grouped[cat].map((p, i) => {
            const propKey = `${cat} ▸ ${p.displayName}`;
            return (
              <div key={`${p.displayName}-${i}`} className="flex items-start gap-2 py-1 group">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{p.displayName}</div>
                  <div className="font-mono break-all" style={{ color: 'var(--text-primary)' }}>{String(p.displayValue ?? '—')}</div>
                </div>
                {p.displayValue !== undefined && p.displayValue !== null && p.displayValue !== '' && (
                  <button onClick={() => onAddFilter(propKey, String(p.displayValue))}
                    className="opacity-0 group-hover:opacity-100 px-1.5 py-0.5 text-[9px] rounded bg-steel-500/15 text-steel-500 hover:bg-steel-500/25 transition shrink-0"
                    title="Filter tree by this value">
                    Filter
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Translation progress bar
// ═══════════════════════════════════════════════════════════════
function ProgressBar({ pct, label, model }) {
  const steps = [
    { id: 'upload', label: 'Uploaded' },
    { id: 'translate', label: 'Translating SVF2' },
    { id: 'extract', label: 'Extracting metadata' },
    { id: 'ready', label: 'Ready' },
  ];
  const stepIdx = pct >= 100 ? 3 : pct >= 80 ? 2 : pct >= 1 ? 1 : 0;
  const stepName = steps[stepIdx].label;
  return (
    <div className="rounded-xl border p-5" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }} data-testid="model-progress">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-steel-500"/>
          <div>
            <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{stepName} on the Panorix model service…</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{model?.modelName || '3D Model'}{label ? ` · ${label}` : ' · waiting for first manifest poll'}</div>
          </div>
        </div>
        <div className="font-mono text-lg tabular-nums" style={{ color: 'var(--text-primary)' }}>{pct}%</div>
      </div>
      <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: 'var(--surface-bg)' }}>
        <div className="h-full bg-gradient-to-r from-steel-500 via-steel-400 to-ember-500 transition-all duration-700 ease-out"
          style={{ width: `${Math.max(2, pct)}%` }}/>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {steps.map((s, i) => (
          <div key={s.id} className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em]"
            style={{ color: i <= stepIdx ? 'var(--text-primary)' : 'var(--text-muted)' }}>
            <div className="w-2 h-2 rounded-full" style={{
              background: i < stepIdx ? '#0E7C6B' : i === stepIdx ? '#F26B1F' : 'var(--border-color)',
              boxShadow: i === stepIdx ? '0 0 0 3px rgba(242,107,31,0.25)' : 'none',
            }}/>
            <span className="truncate">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════
const VOCAB = {
  piping:     { title: 'Piping 3D Model & Allocation',     itemLabel: 'lines / spools',     placeholder: 'Pipe Class, Line Number, Service…',
    mapping: [
      { key: 'lineKey',     label: 'Line Number',     required: true,  hint: 'Master entity — every spool rolls up to a Line' },
      { key: 'spoolKey',    label: 'Spool ID',        required: false, hint: 'Optional — sub-entity within a line' },
      { key: 'areaKey',     label: 'Area',            required: false },
      { key: 'serviceKey',  label: 'Service',         required: false },
      { key: 'sizeKey',     label: 'Size',            required: false },
      { key: 'agUgKey',     label: 'AG / UG',         required: false },
    ],
    extractCTA: 'Populate Lines & Spools',
  },
  structural: { title: 'Structural 3D Model & Allocation', itemLabel: 'tags / mark numbers', placeholder: 'Tag, Member Type, Steel Grade…',
    mapping: [
      { key: 'tagKey',          label: 'Tag Number',     required: true,  hint: 'Master entity — column, beam, brace identifier' },
      { key: 'markKey',         label: 'Mark Number',    required: false, hint: 'Optional — shop fabrication mark' },
      { key: 'areaKey',         label: 'Area',           required: false },
      { key: 'memberTypeKey',   label: 'Member Type',    required: false },
      { key: 'gradeKey',        label: 'Steel Grade',    required: false },
      { key: 'elevationKey',    label: 'Elevation',      required: false },
    ],
    extractCTA: 'Populate Tags & Mark Numbers',
  },
};

// (legacy RIGHT_TABS removed — Browser & Properties are now side-by-side panels)

// ═══════════════════════════════════════════════════════════════
// Mapping sub-components: Category → Property → Values cascade
// ═══════════════════════════════════════════════════════════════

/**
 * Helper: derive `{ categories: { catName: { propName: { uniqueValues:[], dbIdsByValue:{} } } } }`
 * from the flat `properties` map keyed by `"Cat ▸ Prop"`.
 */
function _groupByCategory(properties) {
  const out = {};
  for (const [key, info] of Object.entries(properties || {})) {
    const cat = info.category || 'General';
    (out[cat] ||= {})[info.name] = info;
  }
  return out;
}

function GlobalFilterPicker({ mapping, setMapping, properties }) {
  const cats = useMemo(() => _groupByCategory(properties), [properties]);
  const gf = mapping._globalFilter || { category: '', property: '', values: [] };
  const propsInCat = gf.category ? Object.keys(cats[gf.category] || {}).sort() : [];
  const valuesForProp = gf.category && gf.property ? (cats[gf.category]?.[gf.property]?.uniqueValues || []) : [];
  const dbIdsByValue = gf.category && gf.property ? (cats[gf.category]?.[gf.property]?.dbIdsByValue || {}) : {};

  const update = (patch) => setMapping(m => ({
    ...m,
    _globalFilter: { ...gf, ...patch },
  }));

  const toggleValue = (v) => {
    const cur = new Set(gf.values || []);
    if (cur.has(v)) cur.delete(v); else cur.add(v);
    update({ values: Array.from(cur) });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      <div>
        <Label className="text-eyebrow">Category</Label>
        <select value={gf.category || ''}
          onChange={(e) => update({ category: e.target.value, property: '', values: [] })}
          className="w-full h-8 px-2 mt-1 text-xs rounded-md border bg-transparent"
          style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', background: 'var(--input-bg)' }}
          data-testid="gf-category">
          <option value="">— pick category —</option>
          {Object.keys(cats).sort().map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-eyebrow">Property</Label>
        <select value={gf.property || ''} disabled={!gf.category}
          onChange={(e) => update({ property: e.target.value, values: [] })}
          className="w-full h-8 px-2 mt-1 text-xs rounded-md border bg-transparent disabled:opacity-40"
          style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', background: 'var(--input-bg)' }}
          data-testid="gf-property">
          <option value="">— pick property —</option>
          {propsInCat.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-eyebrow">Qualifying Values</Label>
        <div className="mt-1 max-h-32 overflow-auto rounded-md border p-1 text-xs"
          style={{ borderColor: 'var(--border-color)', background: 'var(--input-bg)' }}>
          {valuesForProp.length === 0 && (
            <div className="text-[10px] p-1.5" style={{ color: 'var(--text-muted)' }}>
              {gf.property ? 'No values' : 'Pick a property →'}
            </div>
          )}
          {valuesForProp.slice(0, 200).map(v => {
            const checked = (gf.values || []).includes(v);
            const cnt = (dbIdsByValue[v] || []).length;
            return (
              <label key={String(v)} className="flex items-center gap-1.5 px-1 py-0.5 hover:bg-steel-500/10 rounded cursor-pointer">
                <input type="checkbox" checked={checked} onChange={() => toggleValue(v)}
                  className="accent-ember-500" data-testid={`gf-val-${v}`}/>
                <span className="truncate flex-1" style={{ color: 'var(--text-primary)' }}>{String(v)}</span>
                <span className="font-mono text-[9px] text-steel-500 shrink-0">{cnt}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function IdentifierPicker({ fieldKey, field, mapping, setMapping, properties }) {
  const cats = useMemo(() => _groupByCategory(properties), [properties]);
  const cur = mapping[fieldKey];

  // Normalize legacy shapes into the new two-part shape:
  //   {
  //     nameKey: { category, property },         // value source
  //     category, property, values: []           // filter (where it applies)
  //   }
  let normalized = { nameKey: { category: '', property: '' }, category: '', property: '', values: [] };
  if (typeof cur === 'string' && cur) {
    const m = cur.match(/^(.+?)\s*▸\s*(.+)$/);
    if (m) normalized = { nameKey: { category: m[1], property: m[2] }, category: '', property: '', values: [] };
  } else if (cur && typeof cur === 'object') {
    normalized = {
      nameKey: {
        category: cur.nameKey?.category || '',
        property: cur.nameKey?.property || '',
      },
      category: cur.category || '',
      property: cur.property || '',
      values: Array.isArray(cur.values) ? cur.values : [],
    };
  }

  // Helpers for both sub-pickers
  const nameProps = normalized.nameKey.category ? Object.keys(cats[normalized.nameKey.category] || {}).sort() : [];
  const filterProps = normalized.category ? Object.keys(cats[normalized.category] || {}).sort() : [];
  const filterPropKey = normalized.category && normalized.property ? `${normalized.category} ▸ ${normalized.property}` : null;
  const filterValuesForProp = filterPropKey ? (properties[filterPropKey]?.uniqueValues || []) : [];
  const filterDbIdsByValue = filterPropKey ? (properties[filterPropKey]?.dbIdsByValue || {}) : {};

  // How many actual dbIds match the current filter? (sum across checked values)
  const matchedObjectCount = useMemo(() => {
    if (!filterPropKey || !(normalized.values || []).length) return null;
    const seen = new Set();
    for (const v of normalized.values) {
      for (const id of (filterDbIdsByValue[v] || [])) seen.add(id);
    }
    return seen.size;
  }, [filterPropKey, normalized.values, filterDbIdsByValue]);

  const hasNameKey = !!(normalized.nameKey.category && normalized.nameKey.property);

  const commit = (next) => {
    const hasAny =
      next.nameKey.category || next.nameKey.property ||
      next.category || next.property || (next.values && next.values.length);
    setMapping(m => ({
      ...m,
      [fieldKey]: hasAny
        ? {
            nameKey: { category: next.nameKey.category || '', property: next.nameKey.property || '' },
            category: next.category || '',
            property: next.property || '',
            values: next.values || [],
          }
        : null,
    }));
  };

  const setNameKey = (patch) =>
    commit({ ...normalized, nameKey: { ...normalized.nameKey, ...patch } });
  const setFilter = (patch) =>
    commit({ ...normalized, ...patch });
  const toggleValue = (v) => {
    const set = new Set(normalized.values || []);
    if (set.has(v)) set.delete(v); else set.add(v);
    setFilter({ values: Array.from(set) });
  };

  return (
    <div className="rounded-md border p-2.5 space-y-3" style={{ borderColor: 'var(--border-subtle)' }} data-testid={`identifier-row-${fieldKey}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-eyebrow flex items-center gap-1.5">
          {field.label}{field.required && <span className="text-ember-500">*</span>}
        </Label>
        <div className="flex items-center gap-2 text-[10px] font-mono">
          {!hasNameKey && (
            <span className="px-2 py-0.5 rounded bg-red-500/15 text-red-400 ring-1 ring-red-500/40">
              ⚠ name from required
            </span>
          )}
          {hasNameKey && (
            <span className="text-ember-500">name · {normalized.nameKey.property}</span>
          )}
          {matchedObjectCount != null && (
            <span className="text-verdigris-500">
              filter · {normalized.values.length} value{normalized.values.length === 1 ? '' : 's'} → {matchedObjectCount} object{matchedObjectCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      {/* Sub-row 1 — Name source */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: 'var(--text-muted)' }}>
          Name from <span className="text-ember-500">*</span> — which property gives this {field.label.toLowerCase()} its actual value (e.g. <span className="font-mono">Item ▸ Name</span>)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <select value={normalized.nameKey.category} onChange={(e) => setNameKey({ category: e.target.value, property: '' })}
            className="h-8 px-2 text-xs rounded-md border bg-transparent"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', background: 'var(--input-bg)' }}
            data-testid={`name-cat-${fieldKey}`}>
            <option value="">— category —</option>
            {Object.keys(cats).sort().map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={normalized.nameKey.property} disabled={!normalized.nameKey.category}
            onChange={(e) => setNameKey({ property: e.target.value })}
            className="h-8 px-2 text-xs rounded-md border bg-transparent disabled:opacity-40"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', background: 'var(--input-bg)' }}
            data-testid={`name-prop-${fieldKey}`}>
            <option value="">— property —</option>
            {nameProps.map(p => {
              const k = `${normalized.nameKey.category} ▸ ${p}`;
              const n = properties[k]?.uniqueValues?.length ?? '';
              return <option key={p} value={p}>{p}{n ? ` (${n} distinct)` : ''}</option>;
            })}
          </select>
        </div>
      </div>

      {/* Sub-row 2 — Qualifying filter (optional) */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: 'var(--text-muted)' }}>
          Filter when — restrict which objects qualify (optional, e.g. <span className="font-mono">PDMS Attribute ▸ Type ∈ [STRU]</span>)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.2fr] gap-2">
          <select value={normalized.category} onChange={(e) => setFilter({ category: e.target.value, property: '', values: [] })}
            className="h-8 px-2 text-xs rounded-md border bg-transparent"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', background: 'var(--input-bg)' }}
            data-testid={`id-cat-${fieldKey}`}>
            <option value="">— category —</option>
            {Object.keys(cats).sort().map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={normalized.property} disabled={!normalized.category} onChange={(e) => setFilter({ property: e.target.value, values: [] })}
            className="h-8 px-2 text-xs rounded-md border bg-transparent disabled:opacity-40"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', background: 'var(--input-bg)' }}
            data-testid={`id-prop-${fieldKey}`}>
            <option value="">— property —</option>
            {filterProps.map(p => {
              const k = `${normalized.category} ▸ ${p}`;
              const n = properties[k]?.uniqueValues?.length ?? '';
              return <option key={p} value={p}>{p}{n ? ` (${n})` : ''}</option>;
            })}
          </select>
          <div className="max-h-28 overflow-auto rounded-md border p-1 text-xs"
            style={{ borderColor: 'var(--border-color)', background: 'var(--input-bg)' }}>
            {!filterPropKey && <div className="text-[10px] p-1.5" style={{ color: 'var(--text-muted)' }}>Qualifying values — pick a property →</div>}
            {filterPropKey && filterValuesForProp.length === 0 && <div className="text-[10px] p-1.5" style={{ color: 'var(--text-muted)' }}>No values</div>}
            {filterPropKey && filterValuesForProp.slice(0, 200).map(v => {
              const checked = (normalized.values || []).includes(v);
              const cnt = (filterDbIdsByValue[v] || []).length;
              return (
                <label key={String(v)} className="flex items-center gap-1.5 px-1 py-0.5 hover:bg-steel-500/10 rounded cursor-pointer">
                  <input type="checkbox" checked={checked} onChange={() => toggleValue(v)}
                    className="accent-ember-500" data-testid={`id-val-${fieldKey}-${v}`}/>
                  <span className="truncate flex-1" style={{ color: 'var(--text-primary)' }}>{String(v)}</span>
                  <span className="font-mono text-[9px] text-steel-500 shrink-0">{cnt}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <p className="text-[10px]" style={{ color: hasNameKey ? 'var(--text-muted)' : 'rgb(248,113,113)' }}>
        {!hasNameKey
          ? `→ Pick a "Name from" property above — without it, no ${field.label.toLowerCase()} rows will be created (your filter currently matches ${matchedObjectCount ?? 'N'} objects, but they need a property to name them by).`
          : `Each of the ${matchedObjectCount ?? 'matched'} object${matchedObjectCount === 1 ? '' : 's'} becomes a separate ${field.label.toLowerCase()}, named by its ${normalized.nameKey.property}. Duplicate names roll up into the same ${field.label.toLowerCase()}.`}
      </p>
    </div>
  );
}

export default function WorkfrontModelTab({ projectId, moduleKey = 'piping' }) {
  const { isDark } = useTheme();
  const vocab = VOCAB[moduleKey] || VOCAB.piping;

  const [model, setModel] = useState(null);
  const [missingInfo, setMissingInfo] = useState(null);     // W3.4 — what is absent, named
  const [mappingCheck, setMappingCheck] = useState(null);   // W3.4 — the mapping claim
  const [mappingCheckBusy, setMappingCheckBusy] = useState(false);
  const [declareBusy, setDeclareBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);

  const [showUpload, setShowUpload] = useState(false);
  // Orphan self-heal — probe & auto-recover buttons on the orphan overlay.
  const [orphanBusy, setOrphanBusy] = useState(null);        // 'probe' | 'recover' | null
  const [orphanActionMsg, setOrphanActionMsg] = useState(null);  // { type: 'ok'|'error', text: string } | null
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [urnInput, setUrnInput] = useState('');
  const [modelName, setModelName] = useState('');
  const [uploading, setUploading] = useState(false);

  const [viewer, setViewer] = useState(null);
  const viewerRef = useRef(null);
  const [properties, setProperties] = useState({});
  const [propsByDbId, setPropsByDbId] = useState({});
  // Direct-parent map { dbId → parentDbId } extracted from the APS object tree.
  // Used by the backend to walk up the hierarchy and link FRMW marks to their
  // STRU tag ancestors (since marks are children of tag groups, not the same dbId).
  const [parentByDbId, setParentByDbId] = useState({});
  const [propLoading, setPropLoading] = useState(false);
  const [propSearch, setPropSearch] = useState('');
  const [filters, setFilters] = useState([]);
  const [resolvedDbIds, setResolvedDbIds] = useState([]);
  const [selectedDbIds, setSelectedDbIds] = useState([]);

  const [treeSearch, setTreeSearch] = useState('');

  const [selections, setSelections] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [entities, setEntities] = useState([]);

  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [showAllocate, setShowAllocate] = useState(false);
  const [allocForm, setAllocForm] = useState({ name: '', contractor: '', crew: '', plannedStart: '', plannedEnd: '', notes: '' });

  const [showMapping, setShowMapping] = useState(false);
  const [mapping, setMapping] = useState({});
  // Confirmation modal shown when the user clicks Save & Populate — asks
  // whether to Merge (add-only, skip duplicates) or Replace (wipe first).
  const [populatePrompt, setPopulatePrompt] = useState(null); // { existingPrimary, existingSecondary } | null
  // Dialog-local draft. The Mapping dialog always opens BLANK (no pre-selection in any
  // field) regardless of what's currently saved on the server. Save commits the draft
  // to `mapping` + DB; Cancel discards it. The persisted server mapping is only used
  // to drive `handleExtractEntities` outside the dialog.
  const [mappingDraft, setMappingDraft] = useState({});
  const [heatmapOn, setHeatmapOn] = useState(false);
  const [heatmapMode, setHeatmapMode] = useState('fabrication'); // 'workfront' | 'fabrication' | 'erection' | 'mtm_demand'
  const [coloringData, setColoringData] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);  // legend-click filter
  const [lastColoringStamp, setLastColoringStamp] = useState(null);
  // MTM drilldown — derived entity for the popover when a single 3D element is
  // clicked while the heatmap is in `mtm_demand` mode.
  const [mtmPopoverDismissed, setMtmPopoverDismissed] = useState(null);  // remember the dbId user closed so it doesn't reopen on re-render
  const mtmEntity = useMemo(() => {
    if (!heatmapOn || !coloringData) return null;
    if (!Array.isArray(selectedDbIds) || selectedDbIds.length === 0) return null;
    // Use the first selected dbId — single-click maps cleanly to spool/mark/line/tag.
    const firstId = selectedDbIds[0];
    if (mtmPopoverDismissed === firstId) return null;
    const info = (coloringData.coloring || {})[String(firstId)];
    if (!info || !info.entityValue) return null;
    return { dbId: firstId, entityType: info.entityType, entityValue: info.entityValue, status: info.status };
  }, [heatmapOn, coloringData, selectedDbIds, mtmPopoverDismissed]);

  // ────────── data fetchers ──────────
  const fetchModel = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/v1/projects/${projectId}/workfront-model/${moduleKey}`);
      setModel(r.data?.model || null);
      setMissingInfo(r.data?.model ? null : (r.data?.missing || null));
      if (r.data?.model?.propertyMapping) setMapping(r.data.model.propertyMapping);
    } catch { setModel(null); setMissingInfo(null); }
    finally { setLoading(false); }
  }, [projectId, moduleKey]);

  // W3.4 — rendering and property mapping are two separate claims. This asks the
  // server to resolve the saved mapping keys against the property database of the
  // model actually on screen, and reports the answer rather than implying it.
  const verifyMapping = useCallback(async () => {
    setMappingCheckBusy(true); setMappingCheck(null);
    try {
      const r = await api.get(
        `/v1/projects/${projectId}/workfront-model/${moduleKey}/property-mapping/verify`,
        { timeout: 180000 });
      setMappingCheck(r.data || null);
    } catch (e) {
      setMappingCheck({ verified: false, statement: e.response?.data?.detail || 'The check could not be completed.' });
    } finally { setMappingCheckBusy(false); }
  }, [projectId, moduleKey]);

  const declareSubstitution = useCallback(async (sourceModelId) => {
    setDeclareBusy(true);
    try {
      await api.post(`/v1/projects/${projectId}/workfront-model/${moduleKey}/declare-substitution`,
        { sourceModule: '4d', sourceModelId });
      toast.success('Substitution declared — the viewer header now states what is being shown.');
      fetchModel();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not declare the substitution.');
    } finally { setDeclareBusy(false); }
  }, [projectId, moduleKey, fetchModel]);

  const revokeSubstitution = useCallback(async () => {
    setDeclareBusy(true);
    try {
      await api.delete(`/v1/projects/${projectId}/workfront-model/${moduleKey}/declare-substitution`);
      toast.success('Substitution withdrawn.');
      fetchModel();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not withdraw the substitution.');
    } finally { setDeclareBusy(false); }
  }, [projectId, moduleKey, fetchModel]);

  const fetchSelections = useCallback(async () => {
    try { const r = await api.get(`/v1/projects/${projectId}/workfront-model/${moduleKey}/selections`); setSelections(r.data?.selections || []); } catch {}
  }, [projectId, moduleKey]);
  const fetchAllocations = useCallback(async () => {
    try { const r = await api.get(`/v1/projects/${projectId}/workfront-model/${moduleKey}/allocations`); setAllocations(r.data?.allocations || []); } catch {}
  }, [projectId, moduleKey]);
  const fetchEntities = useCallback(async () => {
    try { const r = await api.get(`/v1/projects/${projectId}/workfront-model/${moduleKey}/entities`); setEntities(r.data?.entities || []); } catch {}
  }, [projectId, moduleKey]);

  useEffect(() => { if (projectId) { fetchModel(); fetchSelections(); fetchAllocations(); fetchEntities(); } },
    [projectId, fetchModel, fetchSelections, fetchAllocations, fetchEntities]);

  // ── Orphan self-heal handlers ─────────────────────────────────────────
  // Route to the 4D-side probe / auto-recover endpoints on the shared
  // model behind this workfront tab, then re-fetch so the overlay clears.
  const _fourdModelId = model?.sharedFrom4dModelId || model?.modelId || model?._id;
  const handleWorkfrontProbe = async () => {
    if (!_fourdModelId || orphanBusy) return;
    setOrphanBusy('probe'); setOrphanActionMsg(null);
    try {
      const r = await api.post(`/v1/projects/${projectId}/4d/models/${_fourdModelId}/probe-bucket`);
      if (r.data?.ok && r.data?.reachable && !r.data?.orphaned) {
        setOrphanActionMsg({ type: 'ok', text: 'Model service reachable — refreshing…' });
        setTimeout(fetchModel, 400);
      } else {
        setOrphanActionMsg({ type: 'error', text: `The 3D model service still returns ${r.data?.bucketStatus || '?'}/${r.data?.manifestStatus || '?'} — bucket is genuinely orphaned. Use Auto-recover or Re-upload.` });
      }
    } catch (e) {
      setOrphanActionMsg({ type: 'error', text: e.response?.data?.detail || 'Probe failed.' });
    } finally { setOrphanBusy(null); }
  };
  const handleWorkfrontAutoRecover = async (confirm = false) => {
    if (!_fourdModelId || orphanBusy) return;
    setOrphanBusy('recover'); setOrphanActionMsg(null);
    try {
      const r = await api.post(`/v1/projects/${projectId}/4d/models/${_fourdModelId}/auto-recover${confirm ? '?confirm=true' : ''}`);
      const j = r.data || {};
      if (j.ok) {
        setOrphanActionMsg({ type: 'ok', text: j.message || 'Rebuilding now — SVF2 translation submitted. Refreshing…' });
        setTimeout(fetchModel, 800);
      } else if (j.hasSourceBackup === false) {
        setOrphanActionMsg({ type: 'error', text: 'No local source backup for this model — a one-time re-upload is required. All future orphans will self-heal.' });
      } else if (j.needsConfirmation) {
        // W3.5 — above the configured size an automatic translation needs a human yes,
        // because it spends the client's translation credits.
        setOrphanActionMsg({ type: 'confirm', text: j.message, sizeBytes: j.sizeBytes });
      } else if (j.blockedBy) {
        setOrphanActionMsg({ type: 'queued', text: j.message, blockedBy: j.blockedBy });
      } else {
        setOrphanActionMsg({ type: 'error', text: j.message || 'Auto-recover failed.' });
      }
    } catch (e) {
      setOrphanActionMsg({ type: 'error', text: e.response?.data?.detail || 'Auto-recover failed.' });
    } finally { setOrphanBusy(null); }
  };

  // Poll while TRANSLATING — fast cadence so users see the SVF2 % climb
  // in real time (was 8 s → felt frozen for the first ~10 s).
  useEffect(() => {
    if (!model || model.translationStatus !== 'TRANSLATING') { setPolling(false); return; }
    setPolling(true);
    // Immediate re-fetch (some manifests update within ~1 s of upload finalize).
    const kickoff = setTimeout(fetchModel, 400);
    const t = setInterval(fetchModel, 2500);
    return () => { clearTimeout(kickoff); clearInterval(t); };
  }, [model, fetchModel]);

  // ────────── upload ──────────
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile && !urnInput.trim()) { toast.error('Provide a file or a URN.'); return; }
    setUploading(true); setUploadProgress(0);
    try {
      if (urnInput.trim()) {
        await api.post(`/v1/projects/${projectId}/workfront-model/${moduleKey}/urn`,
          { urn: urnInput.trim(), modelName: modelName || '3D Model', modelDescription: '' });
        toast.success('Model URN attached.');
        setShowUpload(false); setUploadFile(null); setUrnInput(''); setModelName('');
        await fetchModel();
        setUploading(false);
        return;
      }

      // Direct-to-APS 3-step flow — file bytes never transit our ingress,
      // so Cloudflare body-size / read-timeout can't 502 us on large models.
      const initResp = await api.post(
        `/v1/projects/${projectId}/workfront-model/${moduleKey}/upload/init`,
        { filename: uploadFile.name }
      );
      const init = initResp.data;
      if (!init?.uploadUrl) throw new Error('Upload init failed — no signed URL returned.');

      await new Promise((resolve, reject) => {
        const putXhr = new XMLHttpRequest();
        putXhr.open('PUT', init.uploadUrl);
        putXhr.setRequestHeader('Content-Type', 'application/octet-stream');
        putXhr.timeout = 30 * 60 * 1000;
        putXhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        };
        putXhr.onerror = () => reject(new Error('Direct upload to the 3D model service failed. Check network / firewall.'));
        putXhr.ontimeout = () => reject(new Error('Direct upload timed out after 30 min.'));
        putXhr.onload = () => {
          if (putXhr.status >= 200 && putXhr.status < 300) resolve();
          else reject(new Error(`Upload storage rejected the file (HTTP ${putXhr.status}). ${putXhr.responseText?.slice(0, 200) || ''}`));
        };
        putXhr.send(uploadFile);
      });

      await api.post(
        `/v1/projects/${projectId}/workfront-model/${moduleKey}/upload/complete`,
        {
          bucketKey: init.bucketKey, objectKey: init.objectKey,
          uploadKey: init.uploadKey, urn: init.urn,
          model_name: modelName || uploadFile.name,
          model_description: '',
        }
      );
      toast.success('Model uploaded — translating…');
      setShowUpload(false); setUploadFile(null); setUrnInput(''); setModelName('');
      await fetchModel();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    }
    finally { setUploading(false); setUploadProgress(0); }
  };

  // ────────── viewer ready → extract props ──────────
  const handleViewerReady = useCallback(async (v) => {
    setViewer(v);
    setPropLoading(true);
    const { properties: props, propsByDbId: byId } = await extractAll(v);
    setProperties(props); setPropsByDbId(byId);
    // Build direct-parent map from the APS object tree (used for hierarchical
    // mark-to-tag linking, e.g. FRMW children of /EQUIPRACK).
    const parentMap = await new Promise((resolve) => {
      try {
        v.getObjectTree((tree) => {
          const m = {};
          const dive = (id) => {
            tree.enumNodeChildren(id, (child) => {
              m[child] = id;
              dive(child);
            });
          };
          dive(tree.getRootId());
          resolve(m);
        }, () => resolve({}));
      } catch { resolve({}); }
    });
    setParentByDbId(parentMap);
    setPropLoading(false);
  }, [model]);

  // ────────── filter resolution ──────────
  useEffect(() => {
    if (!viewer || filters.length === 0) { setResolvedDbIds([]); return; }
    const sets = filters.filter(f => f.propKey && f.value !== '').map(f => new Set(properties[f.propKey]?.dbIdsByValue?.[f.value] || []));
    if (sets.length === 0) { setResolvedDbIds([]); return; }
    let inter = sets[0];
    for (let i = 1; i < sets.length; i++) inter = new Set([...inter].filter(x => sets[i].has(x)));
    const ids = Array.from(inter);
    setResolvedDbIds(ids);
    try {
      if (ids.length > 0) { viewer.isolate(ids); viewer.select(ids); viewer.fitToView(ids); }
      else { viewer.isolate([]); viewer.clearSelection(); }
    } catch {}
  }, [filters, properties, viewer]);

  // ────────── status heatmap (applies coloring + visibility) ──────────
  const _applyColoring = useCallback((c, sFilter) => {
    if (!viewer) return;
    const model = viewer.model;
    if (!model) { console.warn('[heatmap] viewer.model not ready'); return; }
    try {
      const THREE = window.THREE;
      const applicable = new Set(c.applicableDbIds || []);

      // Reset prior theming/isolation state — each call defensively wrapped because
      // APS Viewer can throw cross-origin errors that React's dev overlay surfaces as
      // an opaque "Script error.".
      try { viewer.clearThemingColors(model); } catch (e) { /* noop */ }
      try { viewer.showAll && viewer.showAll(); } catch (e) { /* noop */ }

      const isFiltered = !!sFilter;
      const highlightSet = isFiltered
        ? new Set(Object.entries(c.coloring || {})
            .filter(([, info]) => info.status === sFilter)
            .map(([dbStr]) => parseInt(dbStr, 10)))
        : applicable;

      let applied = 0;
      for (const [dbStr, info] of Object.entries(c.coloring || {})) {
        const dbId = parseInt(dbStr, 10);
        if (!highlightSet.has(dbId)) continue;
        const hex = info.colorHex || '#1E4E8C';
        const r2 = parseInt(hex.slice(1, 3), 16) / 255;
        const g2 = parseInt(hex.slice(3, 5), 16) / 255;
        const b2 = parseInt(hex.slice(5, 7), 16) / 255;
        try {
          viewer.setThemingColor(dbId, new THREE.Vector4(r2, g2, b2, 1), model, true);
          applied++;
        } catch (e) { /* noop */ }
      }

      // Ghost non-applicable items using isolate (the proper APS-7 transparency API).
      if (highlightSet.size > 0) {
        try { viewer.isolate(Array.from(highlightSet), model); } catch (e) { /* noop */ }
      }
      try {
        if (viewer.impl && typeof viewer.impl.setGhostingBrightness === 'function') {
          viewer.impl.setGhostingBrightness(true);
        }
      } catch (e) { /* noop */ }
      try { viewer.impl && viewer.impl.invalidate(true, true, true); } catch (e) { /* noop */ }

      console.log(`[heatmap] applied ${applied} theming colors, isolated ${highlightSet.size} dbIds`);
    } catch (e) { console.warn('coloring apply error', e); }
  }, [viewer]);

  const applyHeatmap = useCallback(async (silent = false) => {
    if (!viewer) return;
    const requestedMode = heatmapMode;  // snapshot the mode at request time
    try {
      const r = await api.get(
        `/v1/projects/${projectId}/workfront-model/${moduleKey}/status-coloring`,
        { params: { mode: requestedMode } }
      );
      // Reject stale responses if the user already switched to a different mode
      // between request and response (race condition on rapid toggling).
      if (requestedMode !== heatmapMode) return;
      const c = r.data || { coloring: {}, applicableDbIds: [], lastUpdated: null };
      if (silent && c.lastUpdated && c.lastUpdated === lastColoringStamp && c.mode === coloringData?.mode) return;
      setLastColoringStamp(c.lastUpdated || null);
      setColoringData(c);
      _applyColoring(c, statusFilter);
      if (!silent) toast.success(`Heatmap (${requestedMode}) on — ${Object.keys(c.coloring || {}).length} objects coloured.`);
    } catch (err) {
      if (!silent) toast.error('Heatmap failed: ' + (err.response?.data?.detail || err.message));
    }
  }, [projectId, moduleKey, viewer, heatmapMode]);

  const clearHeatmap = useCallback(() => {
    if (!viewer) return;
    const model = viewer.model;
    if (!model) return; // model not loaded yet → nothing to clear
    try {
      viewer.clearThemingColors(model);
      try { viewer.isolate([], model); } catch {}  // un-isolate → all opaque again
      try { viewer.showAll(); } catch {}
    } catch {}
    setColoringData(null);
    setStatusFilter(null);
    setLastColoringStamp(null);
  }, [viewer]);

  useEffect(() => {
    if (heatmapOn) applyHeatmap(false); else clearHeatmap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatmapOn, heatmapMode]);

  // Reset legend filter and clear stale palette/data the moment the mode changes,
  // so the inline legend doesn't show chips from the previous mode while the new
  // status-coloring response is in flight.
  useEffect(() => {
    setStatusFilter(null);
    setColoringData(null);
    setLastColoringStamp(null);
  }, [heatmapMode]);

  // ── Reverse cross-link: auto-poll status-coloring while heatmap is ON ──
  // Deps include `heatmapMode` + `applyHeatmap` so the interval closure
  // rebinds whenever the user switches mode — otherwise the stale poll
  // silently overwrites the freshly-painted coloring with the old mode's
  // response (bug: fabrication paint kept getting overwritten by workfront).
  useEffect(() => {
    if (!heatmapOn) return;
    const t = setInterval(() => applyHeatmap(true), 15000);
    return () => clearInterval(t);
  }, [heatmapOn, heatmapMode, applyHeatmap, statusFilter]);

  // ── Reapply coloring whenever statusFilter changes (legend click) ──
  useEffect(() => {
    if (!heatmapOn || !coloringData) return;
    _applyColoring(coloringData, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // ────────── tree filter dbIds (used to grey out non-matching tree nodes) ──────────
  const filteredDbIds = resolvedDbIds.length > 0 ? resolvedDbIds : [];

  // ────────── filter ops ──────────
  const filteredPropKeys = useMemo(() => {
    const q = propSearch.toLowerCase().trim();
    return Object.keys(properties).filter(k => !q || k.toLowerCase().includes(q)).sort();
  }, [properties, propSearch]);
  const addFilterFromKey = (propKey, value = '') => setFilters(prev => [...prev, { propKey, value }]);
  const removeFilter = (i) => setFilters(prev => prev.filter((_, idx) => idx !== i));
  const updateFilter = (i, value) => setFilters(prev => prev.map((f, idx) => idx === i ? { ...f, value } : f));

  // ────────── save / allocate / extract ──────────
  const handleSaveSelection = async () => {
    if (!saveName.trim()) { toast.error('Please name this selection.'); return; }
    if (resolvedDbIds.length === 0) { toast.error('No objects matched.'); return; }
    try {
      await api.post(`/v1/projects/${projectId}/workfront-model/${moduleKey}/selections`, {
        name: saveName.trim(), criteria: filters, dbIds: resolvedDbIds, summary: { itemCount: resolvedDbIds.length },
      });
      toast.success('Selection saved.');
      setShowSave(false); setSaveName('');
      fetchSelections();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const handleApplySelection = (sel) => {
    if (!viewer) return;
    try {
      viewer.isolate(sel.dbIds || []); viewer.select(sel.dbIds || []); viewer.fitToView(sel.dbIds || []);
      setResolvedDbIds(sel.dbIds || []); setFilters(sel.criteria || []);
      toast.message(`Applied "${sel.name}" — ${(sel.dbIds || []).length} objects`);
    } catch {}
  };
  const handleDeleteSelection = async (id) => {
    if (!window.confirm('Delete this saved selection?')) return;
    try { await api.delete(`/v1/projects/${projectId}/workfront-model/${moduleKey}/selections/${id}`); fetchSelections(); }
    catch { toast.error('Delete failed'); }
  };

  const handleCreateAllocation = async () => {
    if (!allocForm.name.trim()) { toast.error('Allocation name required.'); return; }
    if (resolvedDbIds.length === 0) { toast.error('No objects selected.'); return; }
    try {
      await api.post(`/v1/projects/${projectId}/workfront-model/${moduleKey}/allocations`, {
        ...allocForm, plannedStart: allocForm.plannedStart || null, plannedEnd: allocForm.plannedEnd || null, dbIds: resolvedDbIds,
      });
      toast.success('Allocation created.');
      setShowAllocate(false);
      setAllocForm({ name: '', contractor: '', crew: '', plannedStart: '', plannedEnd: '', notes: '' });
      fetchAllocations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Allocation failed'); }
  };

  /**
   * Compute the closure of pool dbIds + every ancestor reachable via parentByDbId.
   * The backend uses ancestor properties to resolve hierarchical mark→tag linking.
   */
  const _withAncestors = (pool) => {
    const closure = new Set(pool);
    for (const id of pool) {
      let cur = parentByDbId[id];
      while (cur && !closure.has(cur)) { closure.add(cur); cur = parentByDbId[cur]; }
    }
    return Array.from(closure);
  };

  const handleExtractEntities = async () => {
    if (resolvedDbIds.length === 0) { toast.error('No objects selected.'); return; }
    const primaryKey = moduleKey === 'piping' ? 'lineKey' : 'tagKey';
    const primaryLabel = moduleKey === 'piping' ? 'Line Number' : 'Tag Number';
    const m = mapping?.[primaryKey];
    const hasPrimary = (typeof m === 'string' && m) || (m && typeof m === 'object' && (m.property || (m.nameKey && m.nameKey.property)));
    if (!hasPrimary) { toast.error(`Map "${primaryLabel}" identifier first.`); setShowMapping(true); return; }
    try {
      const allDbIds = _withAncestors(resolvedDbIds);
      const subset = {};
      const parentSubset = {};
      for (const id of allDbIds) {
        subset[String(id)] = propsByDbId[id] || {};
        if (parentByDbId[id] != null) parentSubset[String(id)] = parentByDbId[id];
      }
      const r = await api.post(`/v1/projects/${projectId}/workfront-model/${moduleKey}/extract-entities`, {
        dbIds: resolvedDbIds, propertiesByDbId: subset, parentByDbId: parentSubset, mapping,
      });
      const fo = r.data.filteredOut > 0 ? ` · ${r.data.filteredOut} filtered out` : '';
      toast.success(`Created ${r.data.primaryCount} ${r.data.primaryLabel}s and ${r.data.secondaryCount} ${r.data.secondaryLabel}s${fo}.`);
      fetchEntities();
    } catch (err) { toast.error(err.response?.data?.detail || 'Extraction failed'); }
  };

  const handleSaveMapping = async () => {
    try {
      await api.post(`/v1/projects/${projectId}/workfront-model/${moduleKey}/property-mapping`, { mapping: mappingDraft });
      setMapping(mappingDraft);
      toast.success('Property mapping saved.');
      setShowMapping(false);
      fetchModel();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  /**
   * Wipe-only path — deletes every previously-populated entity for this module
   * AND every auto-created master row (createdBy=WORKFRONT_3D).  Useful when
   * the user wants to clear stale data before starting fresh, without having
   * to pick a valid mapping first.  Uses the REPLACE endpoint with no primary
   * name_key so the backend takes the wipe-only branch.
   */
  const handleWipeStaleData = async () => {
    const primaryLabel = moduleKey === 'piping' ? 'lines/spools' : 'tags/marks';
    const primaryEntityType = moduleKey === 'piping' ? 'line' : 'tag';
    const secondaryEntityType = moduleKey === 'piping' ? 'spool' : 'mark';
    const nP = entities.filter(e => e.entityType === primaryEntityType).length;
    const nS = entities.filter(e => e.entityType === secondaryEntityType).length;
    if (nP === 0 && nS === 0) { toast.info('Nothing to clear — no stale data.'); return; }
    if (!window.confirm(`This will DELETE ${nP} ${moduleKey === 'piping' ? 'lines' : 'tags'} and ${nS} ${moduleKey === 'piping' ? 'spools' : 'marks'} for this module + every auto-created master row. Continue?`)) return;
    try {
      const pool = Object.keys(propsByDbId).map(s => parseInt(s, 10));
      const r = await api.post(`/v1/projects/${projectId}/workfront-model/${moduleKey}/extract-entities`, {
        dbIds: pool.length ? pool : [0], propertiesByDbId: {}, parentByDbId: {},
        mapping: {}, mode: 'REPLACE', wipeOnly: true,
      });
      const d = r.data;
      toast.success(`Cleared ${d.replacedPrimary || 0} ${moduleKey === 'piping' ? 'lines' : 'tags'} and ${d.replacedSecondary || 0} ${moduleKey === 'piping' ? 'spools' : 'marks'}. Pick a Name-from property, then Save & Populate.`);
      fetchModel(); fetchEntities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wipe failed');
    }
  };

  /**
   * Save the draft mapping and immediately run entity extraction over the qualified pool
   * (current Property-Filter selection if any, otherwise the entire model). Avoids the user
   * having to first build a Property Filter selection just to access the Populate button.
   */
  const handleSaveAndExtract = async () => {
    const primaryKey = moduleKey === 'piping' ? 'lineKey' : 'tagKey';
    const primaryLabel = moduleKey === 'piping' ? 'Line Number' : 'Tag Number';
    const m = mappingDraft?.[primaryKey];
    const hasPrimary =
      (typeof m === 'string' && m) ||
      (m && typeof m === 'object' && ((m.nameKey && m.nameKey.property) || m.property));
    if (!hasPrimary) {
      toast.error(`Cannot populate — the "${primaryLabel}" field needs a Name-from category + property. Pick one in the first row above, then try again.`);
      // Also focus the offending field so the user immediately sees where to go.
      try {
        const el = document.querySelector(`[data-testid="name-cat-${primaryKey}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus?.();
      } catch {}
      return;
    }
    // If entities already exist for this module, force the user to choose Merge vs Replace.
    const primaryEntityType = moduleKey === 'piping' ? 'line' : 'tag';
    const secondaryEntityType = moduleKey === 'piping' ? 'spool' : 'mark';
    const existingPrimary = entities.filter(e => e.entityType === primaryEntityType).length;
    const existingSecondary = entities.filter(e => e.entityType === secondaryEntityType).length;
    if (existingPrimary > 0 || existingSecondary > 0) {
      setPopulatePrompt({ existingPrimary, existingSecondary });
      return;
    }
    // Fresh project — just run MERGE (equivalent to a first-time populate).
    await _runExtract('MERGE');
  };

  const _runExtract = async (mode) => {
    setPopulatePrompt(null);
    try {
      await api.post(`/v1/projects/${projectId}/workfront-model/${moduleKey}/property-mapping`, { mapping: mappingDraft });
      setMapping(mappingDraft);
      const pool = resolvedDbIds.length > 0 ? resolvedDbIds : Object.keys(propsByDbId).map(s => parseInt(s, 10));
      if (pool.length === 0) { toast.error('No objects available — model still loading?'); return; }
      const allDbIds = _withAncestors(pool);
      const subset = {};
      const parentSubset = {};
      for (const id of allDbIds) {
        subset[String(id)] = propsByDbId[id] || {};
        if (parentByDbId[id] != null) parentSubset[String(id)] = parentByDbId[id];
      }
      const r = await api.post(`/v1/projects/${projectId}/workfront-model/${moduleKey}/extract-entities`, {
        dbIds: pool, propertiesByDbId: subset, parentByDbId: parentSubset, mapping: mappingDraft, mode,
      });
      const d = r.data;
      const pl = d.primaryLabel, sl = d.secondaryLabel;
      const parts = [];
      if (mode === 'REPLACE') {
        parts.push(`Replaced ${d.replacedPrimary} ${pl}s and ${d.replacedSecondary} ${sl}s`);
        if (d.replacedMasters && Object.keys(d.replacedMasters).length) {
          const masterTotal = Object.values(d.replacedMasters).reduce((a, b) => a + (b || 0), 0);
          if (masterTotal > 0) parts.push(`cleared ${masterTotal} auto-created master rows`);
        }
        parts.push(`inserted ${d.primaryCount} ${pl}s and ${d.secondaryCount} ${sl}s`);
      } else {
        parts.push(`Added ${d.addedPrimary} new ${pl}s (${d.alreadyExistedPrimary} already existed)`);
        parts.push(`Added ${d.addedSecondary} new ${sl}s (${d.alreadyExistedSecondary} already existed)`);
      }
      if (d.filteredOut > 0) parts.push(`${d.filteredOut} filtered out`);
      toast.success(parts.join(' · '));
      setShowMapping(false);
      fetchModel(); fetchEntities();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save & extract failed'); }
  };

  // ────────── Mapping Preview (client-side dry-run) ──────────
  // Re-computed on every render — uses the same logic as the backend `extract_entities`.
  // Pool = current selection if any, else all dbIds we have properties for.
  const mappingPreview = useMemo(() => {
    if (!showMapping) return null;
    const pool = resolvedDbIds.length > 0 ? resolvedDbIds : Object.keys(propsByDbId).map(s => parseInt(s, 10));
    if (pool.length === 0) return { pool: 0, qualified: 0, primaryUnique: 0, secondaryUnique: 0, missingIdentifier: 0, source: 'none' };

    // Identifier specs now carry BOTH a value source (nameKey) and a filter (category/property/values).
    // Legacy flat-string specs are coerced into a name-source-only shape.
    const _idSpec = (spec) => {
      if (!spec) return null;
      if (typeof spec === 'string') {
        const m = spec.match(/^(.+?)\s*▸\s*(.+)$/);
        return m ? { nameKey: `${m[1]} ▸ ${m[2]}`, filterKey: null, filterValues: new Set() } : null;
      }
      if (typeof spec !== 'object') return null;
      const nk = spec.nameKey;
      const nameKey = (nk && nk.category && nk.property)
        ? `${nk.category} ▸ ${nk.property}`
        : (spec.category && spec.property ? `${spec.category} ▸ ${spec.property}` : null);
      const filterKey = (spec.category && spec.property) ? `${spec.category} ▸ ${spec.property}` : null;
      const filterValues = new Set((spec.values || []).map(String));
      return { nameKey, filterKey, filterValues };
    };

    const primarySpec = _idSpec(mappingDraft[moduleKey === 'piping' ? 'lineKey' : 'tagKey']);
    const secondarySpec = _idSpec(mappingDraft[moduleKey === 'piping' ? 'spoolKey' : 'markKey']);

    const gf = mappingDraft._globalFilter || {};
    const gfKey = (gf.category && gf.property) ? `${gf.category} ▸ ${gf.property}` : null;
    const gfValues = new Set((gf.values || []).map(String));
    const hasGlobalFilter = !!(gfKey && gfValues.size);

    const _passesIdFilter = (props, spec) => {
      if (!spec || !spec.filterKey || spec.filterValues.size === 0) return true;
      const v = props[spec.filterKey];
      return v !== undefined && v !== null && spec.filterValues.has(String(v));
    };

    let qualified = 0, missingId = 0;
    const primarySet = new Set();
    const secondarySet = new Set();
    for (const id of pool) {
      const p = propsByDbId[id] || {};
      if (hasGlobalFilter) {
        const v = p[gfKey];
        if (v === undefined || v === null || !gfValues.has(String(v))) continue;
      }
      qualified++;
      if (primarySpec) {
        if (!_passesIdFilter(p, primarySpec)) continue;  // doesn't qualify as a primary
        const pv = primarySpec.nameKey ? p[primarySpec.nameKey] : undefined;
        if (pv === undefined || pv === null || pv === '') missingId++;
        else primarySet.add(String(pv));
      }
      if (secondarySpec) {
        if (!_passesIdFilter(p, secondarySpec)) continue;
        const sv = secondarySpec.nameKey ? p[secondarySpec.nameKey] : undefined;
        if (sv !== undefined && sv !== null && sv !== '') secondarySet.add(String(sv));
      }
    }
    return {
      pool: pool.length,
      qualified,
      primaryUnique: primarySet.size,
      secondaryUnique: secondarySet.size,
      missingIdentifier: missingId,
      source: resolvedDbIds.length > 0 ? 'selection' : 'model',
    };
  }, [showMapping, mappingDraft, propsByDbId, resolvedDbIds, moduleKey]);

  // ────────── render ──────────
  const hasModel = !!model;
  const isReady = hasModel && model.translationStatus === 'COMPLETE';
  const isTranslating = hasModel && model.translationStatus === 'TRANSLATING';
  const isFailed = hasModel && model.translationStatus === 'FAILED';

  if (loading) {
    return (
      <div className="space-y-3" data-testid="workfront-model-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[480px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`workfront-model-tab-${moduleKey}`}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg surface-elev border" style={{ borderColor: 'var(--border-color)' }}>
            <Box className="w-4 h-4 text-steel-500" />
          </div>
          <div>
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{vocab.title}</h3>
            <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
              Upload model · filter {vocab.itemLabel} by any property · save selections · allocate to crews/IWPs · view status heatmap.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasModel && !isTranslating && (
            <span className="text-[10px] uppercase tracking-[0.18em] font-mono" style={{ color: 'var(--text-muted)' }} data-testid="model-status">
              {model.modelName} · {model.translationStatus}
            </span>
          )}
          {hasModel && isTranslating && (() => {
            const p = typeof model.progressPct === 'number' ? model.progressPct : 0;
            const step = p >= 100 ? 'Ready' : p >= 80 ? 'Extracting metadata' : p >= 1 ? 'Translating SVF2' : 'Queued at the 3D model service';
            return (
              <span className="text-[10.5px] font-mono flex items-center gap-2 px-2 py-1 rounded ring-1"
                style={{ background: 'rgba(242,107,31,0.08)', color: 'var(--text-primary)', borderColor: 'rgba(242,107,31,0.4)' }}
                data-testid="model-status-live">
                <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#F26B1F' }} />
                <span className="uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>{model.modelName}</span>
                <span className="tabular-nums font-semibold" style={{ color: '#F26B1F' }}>{p}%</span>
                <span style={{ color: 'var(--text-muted)' }}>·</span>
                <span className="uppercase tracking-[0.12em]">{step}</span>
              </span>
            );
          })()}
          {model?.apsBucketOrphaned && (
            <span className="text-[10px] px-2 py-1 rounded ring-1 flex items-center gap-1.5"
              style={{ background: 'rgba(239,68,68,0.10)', color: '#EF4444', borderColor: 'rgba(239,68,68,0.4)' }}
              data-testid="model-bucket-orphan-badge"
              title="This model's storage was created by an earlier service API key. Click Replace and re-upload the file to fix.">
              <AlertTriangle className="w-3 h-3" /> Stale model storage — Re-upload in 4D Modelling to auto-fix here
            </span>
          )}
          {isReady && (
            <>
              <Button size="sm" variant="outline" onClick={() => {
                // Open the dialog pre-loaded with the currently saved mapping so the
                // user can review / edit it. Editing happens on the draft only — Save
                // commits, Cancel discards.
                setMappingDraft(mapping ? JSON.parse(JSON.stringify(mapping)) : {});
                setShowMapping(true);
              }} data-testid="mapping-btn">
                <SettingsIcon className="w-3.5 h-3.5"/> Mapping
              </Button>
              {/* Heatmap segmented control — Workfront / Fabrication / Erection / Off */}
              <div className="inline-flex items-center rounded-md border overflow-hidden" style={{ borderColor: 'var(--border-color)' }} data-testid="heatmap-mode-group">
                <button
                  onClick={() => setHeatmapOn(false)}
                  className="px-2.5 py-1 text-[11px] font-medium transition-colors"
                  style={{
                    background: !heatmapOn ? '#143A6B' : 'transparent',
                    color: !heatmapOn ? '#fff' : 'var(--text-muted)',
                  }}
                  data-testid="heatmap-off">
                  Off
                </button>
                {[
                  { key: 'workfront',   label: 'Workfront' },
                  { key: 'soft_alloc',  label: 'Soft Alloc' },
                  { key: 'hard_alloc',  label: 'Hard Alloc' },
                  { key: 'fabrication', label: 'Fabrication' },
                  { key: 'erection',    label: 'Erection' },
                  { key: 'mtm_demand',  label: 'MTM Demand' },
                ].map(opt => {
                  const active = heatmapOn && heatmapMode === opt.key;
                  return (
                    <button key={opt.key}
                      onClick={() => { setHeatmapMode(opt.key); setHeatmapOn(true); }}
                      className="px-2.5 py-1 text-[11px] font-medium transition-colors"
                      style={{
                        background: active ? '#F26B1F' : 'transparent',
                        color: active ? '#fff' : 'var(--text-muted)',
                        borderLeft: '1px solid var(--border-color)',
                      }}
                      data-testid={`heatmap-${opt.key}`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          <Button size="sm" variant="outline" onClick={fetchModel} data-testid="model-refresh-btn"><RefreshCw className="w-3.5 h-3.5"/> Refresh</Button>
          <Button size="sm" variant={hasModel ? 'outline' : 'default'} onClick={() => setShowUpload(true)} data-testid="model-upload-btn">
            <Upload className="w-3.5 h-3.5"/> {hasModel ? 'Replace' : 'Upload Model'}
          </Button>
        </div>
      </div>

      {/* W3.4 — model provenance is recorded on the model document and shown in
          the admin connector console. By the owner's instruction (6 Jun 2026) the
          substitution banner and the property-mapping verdict panel are not
          surfaced to workfront users; rendering is unchanged. */}

      {/* Empty / Translating / Failed states */}
      {!hasModel && missingInfo && (
        <div className="rounded-xl border p-8 text-center" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}
          data-testid="model-missing-state">
          <Box className="w-10 h-10 mx-auto mb-3 text-steel-500/70" />
          <h4 className="text-base font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
            No model is being shown for this workfront
          </h4>
          <p className="text-sm mb-4 max-w-xl mx-auto" style={{ color: 'var(--text-muted)' }}
            data-testid="model-missing-statement">
            {missingInfo.statement}
          </p>
          {Array.isArray(missingInfo.substituteCandidates) && missingInfo.substituteCandidates.length > 0 && (
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: 'var(--text-muted)' }}>
                Declare a substitute instead
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {missingInfo.substituteCandidates.filter(c => !c.orphaned).map(c => (
                  <Button key={c.sourceModelId} size="sm" variant="outline" disabled={declareBusy}
                    onClick={() => declareSubstitution(c.sourceModelId)}
                    data-testid={`declare-substitute-${c.sourceModelId}`}>
                    Show the 4D “{c.modelName}”
                  </Button>
                ))}
              </div>
            </div>
          )}
          <Button onClick={() => setShowUpload(true)} variant="default" data-testid="missing-upload-btn">
            <Upload className="w-4 h-4" /> Upload a dedicated model
          </Button>
        </div>
      )}

      {!hasModel && !missingInfo && (
        <div className="rounded-xl border p-8 text-center" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }} data-testid="model-empty-state">
          <Box className="w-10 h-10 mx-auto mb-3 text-steel-500/70"/>
          <h4 className="text-base font-medium mb-1" style={{ color: 'var(--text-primary)' }}>No 3D model attached yet</h4>
          <p className="text-sm mb-5 max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
            Upload an IFC, RVT, NWD, or DWG file — or paste an existing model URN. Once translated, browse the model tree,
            filter {vocab.itemLabel} by any property, then allocate to workfronts.
          </p>
          <Button onClick={() => setShowUpload(true)} variant="default" data-testid="empty-upload-btn">
            <Upload className="w-4 h-4"/> Upload 3D Model
          </Button>
        </div>
      )}

      {isTranslating && <ProgressBar pct={model.progressPct || 0} label={model.progressLabel} model={model}/>}

      {isFailed && (
        <div className="rounded-xl border p-6 flex items-center gap-3" style={{ borderColor: 'rgba(122,31,31,0.4)', background: 'rgba(122,31,31,0.08)' }}>
          <AlertTriangle className="w-5 h-5 text-crimson-500"/>
          <div className="flex-1">
            <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Translation failed</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Try IFC, RVT, NWD, or DWG. {model.progressLabel}</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowUpload(true)}>Try again</Button>
        </div>
      )}

      {/* Ready — main 2-column layout */}
      {isReady && (
        <div className="space-y-4">
          {/* Always-visible legend strip — appears whenever a heatmap mode is active.
              Falls back to a mode-specific default palette while the status-coloring
              response is still in flight, so the chip set never shows stale data from
              the previous mode. */}
          {heatmapOn && (() => {
            const FALLBACK_PALETTES = {
              workfront: { UNALLOCATED:'#A855F7', PARTIAL_SOFT:'#EAB308', READY_SOFT:'#06B6D4', PO_READY:'#6366F1', PARTIAL_HARD:'#F97316', FULLY_ALLOCATED:'#22C55E', BLOCKED:'#DC2626' },
              soft_alloc: { UNALLOCATED:'#94A3B8', PARTIAL_SOFT:'#EAB308', READY_SOFT:'#06B6D4' },
              hard_alloc: { PO_READY:'#6366F1', PARTIAL_HARD:'#F97316', FULLY_ALLOCATED:'#22C55E', BLOCKED:'#DC2626' },
              fabrication: moduleKey === 'structural'
                ? { NOT_STARTED:'#94A3B8', CUTTING_AND_FITTING:'#3B82F6', WELDING:'#F59E0B', SURFACE_TREATMENT:'#8B5CF6', DISPATCH:'#10B981' }
                : { NOT_STARTED:'#94A3B8', FIT_UP:'#3B82F6', WELDING:'#F59E0B', NDT:'#8B5CF6', PAINTING:'#EC4899', DISPATCHED:'#10B981' },
              erection: { LOCKED:'#EF4444', READY:'#3B82F6', ERECTED:'#22C55E' },
              mtm_demand: { RESERVED:'#22C55E', OPEN_DEMAND:'#F59E0B', STOCKOUT:'#EF4444', NO_SIGNAL:'#475569' },
            };
            const liveMatch = coloringData?.mode === heatmapMode;
            const palette = liveMatch ? (coloringData?.palette || FALLBACK_PALETTES[heatmapMode]) : FALLBACK_PALETTES[heatmapMode];
            return (
            <div className="rounded-lg border px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-2"
              style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}
              data-testid="status-legend-inline">
              <span className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: 'var(--text-muted)' }}>
                {heatmapMode} status
              </span>
              {Object.entries(palette).map(([k, hex]) => {
                const count = liveMatch
                  ? Object.values(coloringData?.coloring || {}).filter(v => v.status === k).length
                  : 0;
                const active = statusFilter === k;
                return (
                  <button key={k} onClick={() => setStatusFilter(active ? null : k)}
                    className="flex items-center gap-1.5 px-2 py-1 rounded transition hover:opacity-80"
                    style={{
                      background: active ? `${hex}30` : 'transparent',
                      border: active ? `1px solid ${hex}` : '1px solid transparent',
                    }}
                    data-testid={`legend-chip-${k}`}>
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: hex }}/>
                    <span className="text-[10px] font-medium" style={{ color: 'var(--text-primary)' }}>
                      {k.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[9px] font-mono tabular-nums" style={{ color: 'var(--text-muted)' }}>
                      ({count})
                    </span>
                  </button>
                );
              })}
              {statusFilter && (
                <button onClick={() => setStatusFilter(null)}
                  className="text-[10px] px-2 py-1 rounded transition"
                  style={{ color: '#F26B1F' }} data-testid="legend-clear">
                  Clear filter
                </button>
              )}
            </div>
            );
          })()}

          {/* Viewer (full width) */}
          <div className="relative rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-color)', minHeight: 540, background: isDark ? '#0A0E1A' : '#1F2937' }}>
            <WorkfrontViewer
              key={model.apsModelUrn + ':' + (model.apsBucketOrphaned ? 'orphan' : 'ok')}
              projectId={projectId} moduleKey={moduleKey}
              modelUrn={model.apsBucketOrphaned ? null : model.apsModelUrn} translationStatus={model.translationStatus}
              isDark={isDark} viewerRef={viewerRef}
              onViewerReady={handleViewerReady}
              onSelect={(ids) => setSelectedDbIds(ids || [])}
            />
            {model.apsBucketOrphaned && (
              <div className="absolute inset-0 flex items-center justify-center p-6 z-10" style={{ background: 'rgba(10,14,26,0.85)' }} data-testid="workfront-bucket-orphan">
                <div className="max-w-lg text-center space-y-4">
                  <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Model unreachable — model-service bucket ownership changed</h3>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    This model was uploaded with an older 3D model service API key. The new key can't read the old bucket, so the 3D model service returns 401/403 on every viewer request.
                  </p>
                  {model.hasSourceBackup ? (
                    <p className="text-[11px] text-emerald-400/90">
                      <b>Auto-recovery available.</b> Panorix kept a copy of the source file — one click re-uploads it to a fresh bucket owned by the current service credentials and reconnects every workfront + interface link automatically.
                    </p>
                  ) : (
                    <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      <b>Legacy model — no source backup.</b> A one-time re-upload in the 4D Modelling module fixes this permanently for every future rotation.
                    </p>
                  )}
                  {orphanActionMsg && (
                    <div data-testid="workfront-orphan-action-wrap">
                      <p className={`text-[11px] rounded px-2 py-1 border ${orphanActionMsg.type === 'error' ? 'text-red-400 border-red-500/30 bg-red-500/5' : orphanActionMsg.type === 'confirm' || orphanActionMsg.type === 'queued' ? 'text-amber-400 border-amber-500/30 bg-amber-500/5' : 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5'}`} data-testid="workfront-orphan-action-msg">{orphanActionMsg.text}</p>
                      {orphanActionMsg.type === 'confirm' && (
                        <Button size="sm" variant="outline" className="mt-2"
                          onClick={() => handleWorkfrontAutoRecover(true)} disabled={!!orphanBusy}
                          data-testid="workfront-orphan-confirm-cost">
                          Yes — spend translation credits and rebuild
                        </Button>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 justify-center flex-wrap">
                    {model.hasSourceBackup && (
                      <button onClick={handleWorkfrontAutoRecover} disabled={orphanBusy}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold text-white hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50"
                        style={{ background: '#0E7C6B' }}
                        data-testid="workfront-bucket-orphan-auto-recover">
                        <RefreshCw className={`w-3.5 h-3.5 ${orphanBusy === 'recover' ? 'animate-spin' : ''}`} />
                        {orphanBusy === 'recover' ? 'Recovering…' : 'Auto-recover from backup'}
                      </button>
                    )}
                    <button onClick={handleWorkfrontProbe} disabled={orphanBusy}
                      className="px-3 py-1.5 rounded-md text-xs font-medium border hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-50"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                      title="Ask the 3D model service if the bucket is really orphaned — auto-adopts the bucket if reachable."
                      data-testid="workfront-bucket-orphan-probe">
                      <RefreshCw className={`w-3.5 h-3.5 ${orphanBusy === 'probe' ? 'animate-spin' : ''}`} />
                      {orphanBusy === 'probe' ? 'Probing…' : 'Test model access'}
                    </button>
                    <a href="/project-controls/4d-model"
                      className="px-3 py-1.5 rounded-md text-xs font-semibold text-white hover:opacity-90 inline-flex items-center gap-1.5"
                      style={{ background: '#F26B1F' }}
                      data-testid="workfront-bucket-orphan-open-4d">
                      <Upload className="w-3.5 h-3.5" /> Open 4D Modelling → Upload
                    </a>
                    <button onClick={() => setShowUpload(true)}
                      className="px-3 py-1.5 rounded-md text-xs font-medium border hover:bg-white/5 inline-flex items-center gap-1.5"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}
                      data-testid="workfront-bucket-orphan-replace">
                      Replace here instead
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Floating overlays */}
            {resolvedDbIds.length > 0 && (
              <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-md text-[11px] tracking-[0.12em] uppercase font-mono"
                style={{ background: 'rgba(30,78,140,0.9)', color: '#fff' }} data-testid="resolved-count">
                {resolvedDbIds.length} objects matched
              </div>
            )}
            {heatmapOn && coloringData && (
              <div className="absolute bottom-3 right-3 rounded-md text-[10px] font-mono overflow-hidden"
                style={{ background: 'rgba(10,14,26,0.92)', color: '#fff', maxWidth: 280, border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid="status-legend">
                <div className="flex items-center justify-between px-3 pt-2 pb-1">
                  <div className="uppercase tracking-[0.18em] text-ember-300">
                    {coloringData.mode || heatmapMode} status — click to filter
                  </div>
                  <button onClick={() => applyHeatmap(false)} title="Refresh now"
                    className="p-1 hover:bg-white/10 rounded transition">
                    <RefreshCw className="w-3 h-3"/>
                  </button>
                </div>
                <div className="px-3 pb-2">
                  {Object.entries(coloringData.palette || {}).map(([k, hex]) => {
                    const count = Object.values(coloringData.coloring || {}).filter(v => v.status === k).length;
                    const active = statusFilter === k;
                    return (
                      <button key={k} onClick={() => setStatusFilter(active ? null : k)}
                        className={`flex items-center gap-2 w-full px-1.5 py-1 rounded transition ${active ? 'bg-white/15' : 'hover:bg-white/8'}`}
                        data-testid={`legend-${k}`}>
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: hex, boxShadow: active ? `0 0 0 2px ${hex}` : 'none' }}/>
                        <span className="flex-1 text-left">{k.replace('_',' ')}</span>
                        <span className="font-mono tabular-nums text-zinc-300">{count}</span>
                      </button>
                    );
                  })}
                  {statusFilter && (
                    <button onClick={() => setStatusFilter(null)} className="mt-1 w-full text-center text-[9px] text-ember-300 hover:text-ember-200 transition py-1 border-t border-white/10">
                      Clear filter ({statusFilter.replace('_',' ')})
                    </button>
                  )}
                </div>
                <div className="px-3 py-1.5 border-t border-white/10 text-zinc-400 flex items-center justify-between">
                  <span>Live polling · 15s</span>
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-verdigris-500 animate-pulse-live"/> live</span>
                </div>
              </div>
            )}
            {/* MTM Demand drilldown — click any element in mtm_demand mode to inspect material signal */}
            {mtmEntity && (
              <MTMElementPopover
                projectId={projectId}
                module={moduleKey}
                entityType={mtmEntity.entityType}
                entityValue={mtmEntity.entityValue}
                onClose={() => setMtmPopoverDismissed(mtmEntity.dbId)}
              />
            )}
          </div>

          {/* Browser + Properties — side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Browser */}
            <div className="rounded-xl border p-3 flex flex-col" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5"><ListTree className="w-3.5 h-3.5 text-steel-500"/>
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Model Browser</h4></div>
                <span className="text-[9px] uppercase tracking-[0.12em]" style={{ color: 'var(--text-muted)' }}
                  title="Plain click selects one node. Shift-click selects every descendant.">
                  click · shift-click = subtree
                </span>
              </div>
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}/>
                <Input value={treeSearch} onChange={(e) => setTreeSearch(e.target.value)}
                  placeholder="Search nodes…" className="pl-8 h-8 text-xs" data-testid="tree-search"/>
              </div>
              <ModelTree viewer={viewer} search={treeSearch} filteredDbIds={filteredDbIds} selectedDbIds={selectedDbIds}/>
            </div>

            {/* Properties */}
            <div className="rounded-xl border p-3 flex flex-col" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5"><Info className="w-3.5 h-3.5 text-steel-500"/>
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Properties</h4></div>
              </div>
              <PropertiesPanel viewer={viewer} selectedDbIds={selectedDbIds}
                onAddFilter={(propKey, value) => addFilterFromKey(propKey, value)}/>
            </div>
          </div>

          {/* Property Filter + Action strip + Saved/Entities/Allocations row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Property Filter */}
            <div className="rounded-xl border p-3 lg:col-span-1" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5"><Filter className="w-3.5 h-3.5 text-steel-500"/>
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Property Filter (AND)</h4></div>
                {propLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-steel-500"/>}
              </div>

              {Object.keys(properties).length === 0 && !propLoading && (
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>No properties extracted yet.</div>
              )}

              {Object.keys(properties).length > 0 && (
                <>
                  <div className="relative mb-2">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}/>
                    <Input value={propSearch} onChange={(e) => setPropSearch(e.target.value)}
                      placeholder={`e.g. ${vocab.placeholder}`}
                      className="pl-8 h-8 text-xs" data-testid="property-search"/>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-auto pr-1 mb-3">
                    {filteredPropKeys.slice(0, 60).map((pk) => (
                      <button key={pk} onClick={() => addFilterFromKey(pk)}
                        className="w-full text-left text-[11px] px-2 py-1 rounded-md hover:bg-steel-500/10 transition flex items-center justify-between"
                        style={{ color: 'var(--text-secondary)' }} data-testid={`prop-row-${pk}`}>
                        <span className="truncate">{pk}</span>
                        <span className="font-mono text-[10px] text-steel-500 shrink-0 ml-2">{properties[pk].uniqueValues.length}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {filters.length > 0 && (
                <div className="space-y-2 border-t pt-3" style={{ borderColor: 'var(--border-subtle)' }}>
                  <div className="text-eyebrow">Active filters</div>
                  {filters.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>{f.propKey}</div>
                        <select value={f.value} onChange={(e) => updateFilter(i, e.target.value)}
                          className="w-full h-7 px-2 text-xs rounded-md border bg-transparent"
                          style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} data-testid={`filter-value-${i}`}>
                          <option value="">— pick value —</option>
                          {(properties[f.propKey]?.uniqueValues || []).map(v => (
                            <option key={String(v)} value={v}>{String(v)} ({(properties[f.propKey].dbIdsByValue[v] || []).length})</option>
                          ))}
                        </select>
                      </div>
                      <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => removeFilter(i)}><X className="w-3.5 h-3.5"/></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action + Saved Selections + Extracted Entities + Allocations */}
            <div className="lg:col-span-2 space-y-3">
              {/* Action strip */}
              {resolvedDbIds.length > 0 && (
                <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-mono" style={{ color: 'var(--text-muted)' }}>
                    Selection · <span className="text-ember-500">{resolvedDbIds.length}</span> objects
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setShowSave(true)} data-testid="save-selection-btn"><Save className="w-3 h-3"/> Save</Button>
                    <Button size="sm" variant="outline" onClick={handleExtractEntities} data-testid="extract-entities-btn" className="col-span-2">
                      <Sparkles className="w-3 h-3"/> {vocab.extractCTA}
                    </Button>
                  </div>
                  <Button size="sm" variant="ember" onClick={() => setShowAllocate(true)} className="w-full" data-testid="allocate-btn">
                    <Send className="w-3.5 h-3.5"/> Allocate to Workfront
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Saved Selections */}
                <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-steel-500"/><span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Saved</span></div>
                    <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{selections.length}</span>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-auto pr-1">
                    {selections.length === 0 && <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>None yet.</div>}
                    {selections.map(s => (
                      <div key={s.selectionId} className="flex items-center gap-1 group">
                        <button onClick={() => handleApplySelection(s)} className="flex-1 min-w-0 text-left p-1 hover:bg-steel-500/5 rounded">
                          <div className="text-[11px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>{s.name}</div>
                          <div className="text-[9px] font-mono" style={{ color: 'var(--text-muted)' }}>{s.dbIdCount} obj · {(s.criteria || []).length} filt</div>
                        </button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => handleDeleteSelection(s.selectionId)}><Trash2 className="w-3 h-3 text-crimson-500"/></Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Extracted Entities */}
                <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5 text-steel-500"/>
                      <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {moduleKey === 'piping' ? 'Lines/Spools' : 'Tags/Marks'}
                      </span></div>
                    <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{entities.length}</span>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-auto pr-1">
                    {entities.length === 0 && <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Use {vocab.extractCTA} after selecting.</div>}
                    {entities.slice(0, 60).map((e, i) => (
                      <div key={i} className="flex items-center justify-between p-1 text-[11px]">
                        <span className="truncate" style={{ color: 'var(--text-primary)' }}>
                          <Badge variant={e.entityType === 'line' || e.entityType === 'tag' ? 'on-track' : 'secondary'} className="mr-1.5 text-[8px]">{e.entityType}</Badge>
                          {e.value}{e.parentValue ? ` · ${e.parentValue}` : ''}
                        </span>
                        <span className="font-mono text-[9px] shrink-0" style={{ color: 'var(--text-muted)' }}>{e.dbIdCount}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Allocations */}
                <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5 text-steel-500"/><span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Allocations</span></div>
                    <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{allocations.length}</span>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-auto pr-1">
                    {allocations.length === 0 && <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>None yet.</div>}
                    {allocations.map(a => (
                      <div key={a.allocationId} className="p-1 text-[11px]">
                        <div className="flex items-center justify-between">
                          <span className="truncate font-medium" style={{ color: 'var(--text-primary)' }}>{a.name}</span>
                          <Badge variant="on-track" className="text-[8px]">{a.status || 'ALLOC'}</Badge>
                        </div>
                        <div className="text-[9px] font-mono" style={{ color: 'var(--text-muted)' }}>{a.dbIdCount} obj{a.contractor ? ` · ${a.contractor}` : ''}{a.crew ? ` · ${a.crew}` : ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Upload dialog ─── */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload 3D Model</DialogTitle>
            <DialogDescription className="text-xs">Upload an IFC, RVT, NWD, or DWG file, or paste an model URN.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-3">
            <div><Label className="text-eyebrow">Model name</Label>
              <Input value={modelName} onChange={(e) => setModelName(e.target.value)} placeholder="e.g. Refinery — ISBL — IFC v3" className="mt-1.5" data-testid="upload-model-name"/></div>
            <div><Label className="text-eyebrow">Upload file</Label>
              <Input type="file" accept=".ifc,.rvt,.nwd,.nwc,.dwg,.dwf,.dxf,.step,.stp,.iges,.igs,.skp,.fbx,.obj,.3dm,.dgn,.zip" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="mt-1.5" data-testid="upload-file"/>
              <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Supports IFC, RVT, NWD, DWG and more. Uploaded directly to the 3D model service — no ingress body-size limit.</p></div>
            {uploading && uploadFile && (
              <div className="space-y-1" data-testid="upload-progress-workfront">
                <div className="flex items-center justify-between text-[10px] font-mono">
                  <span>{uploadProgress < 100 ? 'Uploading to the 3D model service…' : 'Finalizing translation job…'}</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'var(--surface-bg)' }}>
                  <div className="h-full bg-gradient-to-r from-steel-500 to-ember-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 my-1"><div className="flex-1 h-px" style={{ background: 'var(--border-color)' }}/><span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-muted)' }}>or</span><div className="flex-1 h-px" style={{ background: 'var(--border-color)' }}/></div>
            <div><Label className="text-eyebrow flex items-center gap-1.5"><LinkIcon className="w-3 h-3"/> Paste model URN</Label>
              <Input value={urnInput} onChange={(e) => setUrnInput(e.target.value)} placeholder="dXJuOmFkc2sub2JqZWN0czpv…" className="mt-1.5 font-mono text-xs" data-testid="upload-urn"/></div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowUpload(false)} disabled={uploading}>Cancel</Button>
              <Button type="submit" variant="default" disabled={uploading} data-testid="upload-submit">
                {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Uploading… {uploadFile ? `${uploadProgress}%` : ''}</> : <><Upload className="w-3.5 h-3.5"/> Attach Model</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── Save selection dialog ─── */}
      <Dialog open={showSave} onOpenChange={setShowSave}>
        <DialogContent className="sm:max-w-sm" data-testid="save-selection-dialog">
          <DialogHeader><DialogTitle>Save Selection</DialogTitle>
            <DialogDescription className="text-xs">{resolvedDbIds.length} objects · {filters.length} filters</DialogDescription></DialogHeader>
          <div><Label className="text-eyebrow">Selection name</Label>
            <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. Area-2 · UG · Priority-1" className="mt-1.5" data-testid="save-name"/></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSave(false)}>Cancel</Button>
            <Button variant="default" onClick={handleSaveSelection} data-testid="save-confirm"><CheckCircle2 className="w-3.5 h-3.5"/> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Allocate dialog ─── */}
      <Dialog open={showAllocate} onOpenChange={setShowAllocate}>
        <DialogContent className="sm:max-w-md" data-testid="allocate-dialog">
          <DialogHeader><DialogTitle>Allocate to Workfront</DialogTitle>
            <DialogDescription className="text-xs">{resolvedDbIds.length} objects will be allocated.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-eyebrow">Allocation name *</Label>
              <Input value={allocForm.name} onChange={(e) => setAllocForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. IWP-PIPE-A2-W12" className="mt-1.5" data-testid="alloc-name"/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-eyebrow">Contractor</Label><Input value={allocForm.contractor} onChange={(e) => setAllocForm(f => ({ ...f, contractor: e.target.value }))} className="mt-1.5" data-testid="alloc-contractor"/></div>
              <div><Label className="text-eyebrow">Crew</Label><Input value={allocForm.crew} onChange={(e) => setAllocForm(f => ({ ...f, crew: e.target.value }))} className="mt-1.5" data-testid="alloc-crew"/></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-eyebrow">Planned start</Label><Input type="date" value={allocForm.plannedStart} onChange={(e) => setAllocForm(f => ({ ...f, plannedStart: e.target.value }))} className="mt-1.5"/></div>
              <div><Label className="text-eyebrow">Planned end</Label><Input type="date" value={allocForm.plannedEnd} onChange={(e) => setAllocForm(f => ({ ...f, plannedEnd: e.target.value }))} className="mt-1.5"/></div>
            </div>
            <div><Label className="text-eyebrow">Notes</Label><Input value={allocForm.notes} onChange={(e) => setAllocForm(f => ({ ...f, notes: e.target.value }))} className="mt-1.5"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAllocate(false)}>Cancel</Button>
            <Button variant="ember" onClick={handleCreateAllocation} data-testid="alloc-confirm"><Send className="w-3.5 h-3.5"/> Allocate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Property → Domain mapping wizard (Global Filter + per-field Identifier) ─── */}
      <Dialog open={showMapping} onOpenChange={setShowMapping}>
        <DialogContent className="sm:max-w-[1100px] w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="mapping-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><SettingsIcon className="w-4 h-4"/> Property → Domain Mapping</DialogTitle>
            <DialogDescription className="text-xs">
              Step 1: pick a <strong>global filter</strong> to qualify which objects are {moduleKey === 'piping' ? 'piping' : 'structural'} items
              (e.g. <span className="font-mono">PDMS Attribute ▸ Type ∈ [FRMW]</span>).{' '}
              Step 2: for each field, pick the property that provides its actual value.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-auto pr-1">
            {/* ── Global Filter section ── */}
            <div className="rounded-lg border p-3" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-eyebrow-ember">Global Filter (optional)</Label>
                {(() => {
                  const gf = mapping._globalFilter || {};
                  if (gf.category && gf.property && (gf.values || []).length > 0)
                    return <span className="text-[10px] font-mono text-verdigris-500">active · {(gf.values || []).length} values</span>;
                  return null;
                })()}
              </div>
              <p className="text-[10px] mb-3" style={{ color: 'var(--text-muted)' }}>
                Only objects matching this rule will be considered when rolling up entities. Leave empty to include everything selected.
              </p>
              <GlobalFilterPicker mapping={mappingDraft} setMapping={setMappingDraft} properties={properties}/>
            </div>

            {/* ── Identifier per field ── */}
            <div className="space-y-3">
              <Label className="text-eyebrow">Identifier per Field</Label>
              {vocab.mapping.map(f => (
                <IdentifierPicker key={f.key} fieldKey={f.key} field={f} mapping={mappingDraft} setMapping={setMappingDraft} properties={properties}/>
              ))}
            </div>
          </div>
          {/* Preview panel — dry-run against current selection (or whole model) */}
          {mappingPreview && (
            <div className="rounded-lg border p-3 mt-3" style={{
              borderColor: mappingPreview.qualified > 0 ? 'rgba(14,124,107,0.4)' : 'rgba(242,107,31,0.4)',
              background: mappingPreview.qualified > 0 ? 'rgba(14,124,107,0.06)' : 'rgba(242,107,31,0.06)',
            }} data-testid="mapping-preview">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-eyebrow-ember">Preview · Dry-Run</Label>
                <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>
                  source: {mappingPreview.source === 'selection' ? 'current selection' : 'entire model'}
                </span>
              </div>
              <div className="text-xs leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                This mapping will{' '}
                <span className="font-mono text-verdigris-500 font-semibold">qualify {mappingPreview.qualified}</span>{' '}
                of <span className="font-mono">{mappingPreview.pool}</span> objects, rolling up to{' '}
                <span className="font-mono text-ember-500 font-semibold">{mappingPreview.primaryUnique}</span>{' '}
                unique {moduleKey === 'piping' ? 'lines' : 'tags'}
                {mappingPreview.secondaryUnique > 0 && (
                  <> and <span className="font-mono text-ember-500 font-semibold">{mappingPreview.secondaryUnique}</span>{' '}
                  unique {moduleKey === 'piping' ? 'spools' : 'mark numbers'}</>
                )}.
                {mappingPreview.missingIdentifier > 0 && (
                  <span className="block mt-1 text-[11px]" style={{ color: '#F26B1F' }}>
                    ⚠ {mappingPreview.missingIdentifier} qualified object{mappingPreview.missingIdentifier === 1 ? '' : 's'} have an empty identifier property and will be skipped.
                  </span>
                )}
                {mappingPreview.qualified === 0 && mappingPreview.pool > 0 && (
                  <span className="block mt-1 text-[11px]" style={{ color: '#F26B1F' }}>
                    No objects matched the filter — try adjusting the qualifying values.
                  </span>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMapping(false)}>Cancel</Button>
            <Button variant="outline" onClick={handleWipeStaleData}
              className="border-red-500/40 text-red-500 hover:bg-red-500/10"
              data-testid="mapping-wipe-stale"
              title="Delete every previously-populated tag/mark/line/spool + auto-created master row for this module. Use this if you're seeing stale data that won't go away.">
              <AlertTriangle className="w-3.5 h-3.5"/> Clear Stale Data
            </Button>
            <Button variant="default" onClick={handleSaveMapping} data-testid="mapping-save"><CheckCircle2 className="w-3.5 h-3.5"/> Save Mapping</Button>
            {(() => {
              const primaryKey = moduleKey === 'piping' ? 'lineKey' : 'tagKey';
              const m = mappingDraft?.[primaryKey];
              const hasPrimary = (typeof m === 'string' && m) ||
                (m && typeof m === 'object' && ((m.nameKey && m.nameKey.property) || m.property));
              return (
                <Button variant="ember" onClick={handleSaveAndExtract} data-testid="mapping-save-extract"
                  disabled={!hasPrimary}
                  title={hasPrimary ? '' : `Pick a Name-from category + property for the ${moduleKey === 'piping' ? 'Line Number' : 'Tag Number'} field above to enable this.`}>
                  <Sparkles className="w-3.5 h-3.5"/> Save &amp; {vocab.extractCTA}
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Populate mode chooser (Merge vs Replace) ─── */}
      <Dialog open={!!populatePrompt} onOpenChange={(v) => { if (!v) setPopulatePrompt(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="populate-mode-dialog">
          <DialogHeader>
            <DialogTitle>Populate {vocab.itemLabel} — how should we reconcile?</DialogTitle>
            <DialogDescription className="text-xs">
              This project already has <b>{populatePrompt?.existingPrimary || 0}</b> {moduleKey === 'piping' ? 'lines' : 'tags'} and{' '}
              <b>{populatePrompt?.existingSecondary || 0}</b> {moduleKey === 'piping' ? 'spools' : 'marks'} populated. Pick how the new run should combine with them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button onClick={() => _runExtract('MERGE')}
              className="w-full text-left rounded-md border px-3 py-2.5 hover:ring-2 hover:ring-verdigris-500/40 transition"
              style={{ borderColor: 'var(--border-color)' }}
              data-testid="populate-mode-merge">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: '#0E7C6B' }} />
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Add to the existing list</div>
              </div>
              <div className="text-[11px] mt-1 pl-4" style={{ color: 'var(--text-muted)' }}>
                Only brand-new {moduleKey === 'piping' ? 'line / spool' : 'tag / mark'} values are inserted. Existing rows are refreshed in-place (dbIds updated, other fields untouched). No duplicates will be created.
              </div>
            </button>
            <button onClick={() => _runExtract('REPLACE')}
              className="w-full text-left rounded-md border px-3 py-2.5 hover:ring-2 hover:ring-red-500/40 transition"
              style={{ borderColor: 'var(--border-color)' }}
              data-testid="populate-mode-replace">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: '#EF4444' }} />
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Replace the complete list</div>
              </div>
              <div className="text-[11px] mt-1 pl-4" style={{ color: 'var(--text-muted)' }}>
                Every previously-populated {moduleKey === 'piping' ? 'line and spool' : 'tag and mark'} for this module is deleted first, then the new set is inserted. Use this after a clean re-mapping.
              </div>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPopulatePrompt(null)} data-testid="populate-mode-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
